/**
 * Modifier Groups JavaScript
 * Handles dynamic field group creation and modifier management
 */

// GUID Generation Helpers
const Helpers = {
    /**
     * Generate a unique ID with specified length using base36
     * @param {number} base - Length of the generated ID (default: 5)
     * @returns {string} Random string of specified length
     */
    uniqueIdWithLength: function(base) {
        if (base === undefined) {
            base = 5;
        }
        return Math.random().toString(36).substr(2, base);
    },

    /**
     * Generate a unique field ID similar to rapf.js implementation
     * Creates a 13-character ID: 8-char timestamp + 5-char random
     * @param {string} n - Prefix string (default: "")
     * @param {boolean} prefix - Add additional random suffix
     * @returns {string} Unique ID string
     */
    uniqueId: function(n, prefix) {
        function padHex(r, c) {
            r = parseInt(r, 10).toString(16);
            if (c < r.length) {
                return r.slice(r.length - c);
            }
            if (c > r.length) {
                return Array(c - r.length + 1).join("0") + r;
            }
            return r;
        }

        if (n === undefined) {
            n = "";
        }
        if (prefix === undefined) {
            prefix = false;
        }

        var i = Math.floor(123456789 * Math.random()) + 1;
        n += padHex(parseInt(((new Date).getTime() / 1000).toString(), 10), 8);
        n += padHex(i, 5);

        if (prefix) {
            n += (10 * Math.random()).toFixed(8).toString();
        }

        return n;
    }
};

function escapeHtml(value) {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function isTruthy(value) {
    return value === true || value === 1 || value === '1' || value === 'true';
}

// Group-level helpers for layout/conditionals serialization
window.MB_ModifierGroups = window.MB_ModifierGroups || {};
MB_ModifierGroups.serializeGroupExtras = function(formEl) {
    let out = { layout_sections: '', conditionals: '' };
    const layoutInput = formEl.querySelector('[name="layout_sections"]');
    const condInput = formEl.querySelector('[name="conditionals"]');
    if (layoutInput) {
        try { out.layout_sections = JSON.stringify(JSON.parse(layoutInput.value || '')); }
        catch(e) { out.layout_sections = layoutInput.value || ''; }
    }
    if (condInput) {
        try { out.conditionals = JSON.stringify(JSON.parse(condInput.value || '')); }
        catch(e) { out.conditionals = condInput.value || ''; }
    }
    return out;
};

// Conditions UI: operator-dependent value control
MB_ModifierGroups.initConditions = function(container) {
    const root = container || document.getElementById('modifier-group-form');
    if (!root) return;
    let categories = [];
    try {
        const dataAttr = root.getAttribute('data-categories');
        if (dataAttr) categories = JSON.parse(dataAttr);
    } catch (e) {}

    function renderValueControl(row, operator, currentValue) {
        const valueCell = row.querySelector('[data-condition-value]');
        if (!valueCell) return;
        valueCell.innerHTML = '';
        if (operator === 'equals' || operator === 'not_equals') {
            const sel = document.createElement('select');
            sel.name = 'condition_value';
            sel.className = 'w-full border rounded px-2 py-1 text-sm';
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Select category';
            sel.appendChild(placeholder);
            categories.forEach(c => {
                const opt = document.createElement('option');
                opt.value = String(c.id);
                opt.textContent = c.name;
                if (currentValue && String(currentValue) === String(c.id)) opt.selected = true;
                sel.appendChild(opt);
            });
            valueCell.appendChild(sel);
        } else if (operator === 'contains' || operator === 'not_contains') {
            const input = document.createElement('input');
            input.type = 'text';
            input.name = 'condition_value';
            input.className = 'w-full border rounded px-2 py-1 text-sm js-select2-tags';
            if (currentValue) input.value = currentValue;
            valueCell.appendChild(input);
            if (window.jQuery && jQuery.fn && jQuery.fn.select2) {
                jQuery(input).select2({ tags: true, tokenSeparators: [','] });
            }
        }
    }

    // Delegate operator change
    root.addEventListener('change', function(e) {
        const el = e.target;
        if (el && el.matches('[name="condition_operator"]')) {
            const row = el.closest('[data-condition-row]') || root;
            renderValueControl(row, el.value, null);
        }
    });

    // Initial render for existing rows
    root.querySelectorAll('[data-condition-row]').forEach(row => {
        const op = row.querySelector('[name="condition_operator"]');
        const valInput = row.querySelector('[name="condition_value"]');
        const currentVal = valInput ? valInput.value : null;
        renderValueControl(row, op ? op.value : 'equals', currentVal);
    });
};

document.addEventListener('DOMContentLoaded', function() {
    MB_ModifierGroups.initConditions();
});

function applyToggleState(toggleInput) {
    if (!toggleInput) {
        return;
    }

    const toggleTrack = toggleInput.nextElementSibling?.querySelector('.toggle-track');
    const toggleThumb = toggleTrack?.querySelector('.toggle-thumb');
    const toggleLabel = toggleInput.closest('label');
    const toggleText = toggleLabel?.querySelector('span:not(.sr-only)');

    if (!toggleTrack || !toggleThumb) {
        return;
    }

    if (toggleInput.checked) {
        toggleTrack.classList.add('bg-blue-500');
        toggleTrack.classList.remove('bg-gray-300');
        toggleThumb.classList.add('translate-x-5');
        toggleThumb.classList.remove('translate-x-0');
        if (toggleText) {
            toggleText.textContent = 'Yes';
        }
    } else {
        toggleTrack.classList.remove('bg-blue-500');
        toggleTrack.classList.add('bg-gray-300');
        toggleThumb.classList.remove('translate-x-5');
        toggleThumb.classList.add('translate-x-0');
        if (toggleText) {
            toggleText.textContent = 'No';
        }
    }
}

// Global variables
let fieldGroupCounter = 0;
let optionCounters = {}; // Track option counters per field group
let draggedFieldGroupId = null;

// Sample options data - would normally load from server
let options = [
    {
        id: 1,
        label: 'Sprinkles',
        price_type: 'none',
        price: 0.25,
        included: false,
        selected: true
    },
    {
        id: 2,
        label: 'Chocolate',
        price_type: 'fixed',
        price: 0.5,
        included: false,
        selected: false
    },
    {
        id: 3,
        label: 'Graham Crackers',
        price_type: 'none',
        price: 0.75,
        included: false,
        selected: false
    },
    {
        id: 4,
        label: 'Oreo',
        price_type: 'none',
        price: 0.75,
        included: false,
        selected: false
    }
];

// Conditions section variables
let conditionGroupCounter = 0;
let conditionRuleCounter = 0;
let conditionData = {};

// Modifier conditions and rules
let modifierConditionCounter = 0;
let modifierRuleCounter = 0;
let modifierConditions = {};
let modifierRules = {};

// Field-level conditions per group
let fieldConditionCounter = 0;
const fieldConditions = {};
const pendingFieldConditions = [];
const fieldConditionGroupCounters = {};

// Advanced condition data
let advancedConditionCounter = 0;

/**
 * Initialize modifier group functionality
 */
async function initializeModifierGroups() {
    // Skip initialization on product edit pages - they use read-only modifier display
    if (isProductEditPage()) {
        console.log('Skipping modifier group initialization on product edit page');
        return;
    }

    // Auto-focus first input
    const groupNameInput = document.querySelector('input[name="group_name"]');
    if (groupNameInput) {
        groupNameInput.focus();
    }

    let hasPrefilledFieldGroups = false;

    if (isModifierGroupEditPage()) {
        try {
            hasPrefilledFieldGroups = await loadExistingFieldGroups();
        } catch (error) {
            console.error('Failed to load existing modifier fields', error);
        }

        // Restore saved group-level conditions
        if (window.groupConditionalsData &&
            window.groupConditionalsData.rules &&
            window.groupConditionalsData.rules.length > 0) {
            window.populateConditionalRules(window.groupConditionalsData);
        }
    }

    if (!hasPrefilledFieldGroups) {
        addNewFieldGroup();
    }

    initializeFieldGroupSorting();
    refreshFieldGroupOrderUI();

    // Initialize sections as open
    const groupDetailsContent = document.getElementById('group-details-content');
    const fieldsContent = document.getElementById('fields-content');

    if (groupDetailsContent) groupDetailsContent.style.display = 'block';
    if (fieldsContent) fieldsContent.style.display = 'block';

    // Setup form validation
    setupFormValidation();

    // Setup group type listeners
    setupGroupTypeListeners();

    updateFieldConditionAvailability();
}

function isModifierGroupEditPage() {
    if (typeof window === 'undefined') {
        return false;
    }
    return /modifiers\/[0-9a-f-]+\/edit/i.test(window.location.pathname);
}

function isProductEditPage() {
    if (typeof window === 'undefined') {
        return false;
    }
    return /products\/[0-9a-f-]+\/edit/i.test(window.location.pathname);
}

async function loadExistingFieldGroups() {
    console.log("loading exsiting field groups...");
    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer) {
        return false;
    }

    // Skip if field groups are already present in the DOM
    if (fieldsContainer.querySelector('[id^="field-group-"]')) {
        return true;
    }

    const serverFieldGroups = getFieldGroupsFromServerData();
    if (serverFieldGroups.length > 0) {
        populateFieldGroups(serverFieldGroups, { skipAutoUpdates: true });
        return true;
    }

    const apiFieldGroups = await fetchFieldGroupsFromApi();
    if (apiFieldGroups.length > 0) {
        populateFieldGroups(apiFieldGroups, { skipAutoUpdates: true });
        return true;
    }

    return false;
}

function getFieldGroupsFromServerData() {
    if (typeof window === 'undefined') {
        return [];
    }

    if (Array.isArray(window.fieldGroupsData) && window.fieldGroupsData.length > 0) {
        return window.fieldGroupsData;
    }

    if (Array.isArray(window.modifiersData) && window.modifiersData.length > 0) {
        return normalizeModifiersToFieldGroups(window.modifiersData);
    }

    return [];
}

async function fetchFieldGroupsFromApi() {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return [];
    }

    const match = window.location.pathname.match(/modifiers\/([0-9a-f-]+)/i);

    if (!match) {
        return [];
    }

    const groupId = match[1];

    try {
        // Use admin-ajax endpoint instead of direct controller route
        const formData = new URLSearchParams();
        formData.append('action', 'get_modifier_group_edit_data');
        formData.append('group_id', groupId);
        if (window.ajaxNonce) {
            formData.append('_ajax_nonce', window.ajaxNonce);
        }

        const response = await fetch('/admin-ajax.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData,
            credentials: 'same-origin'
        });

        if (!response.ok) {
            return [];
        }

        const payload = await response.json();
        if (payload && payload.success && Array.isArray(payload.field_groups)) {
            // Also populate layout and conditionals if present
            if (payload.layout_sections) {
                window.groupLayoutData = payload.layout_sections;
                if (typeof window.populateLayoutFields === 'function') {
                    window.populateLayoutFields(payload.layout_sections);
                }
            }
            if (payload.conditionals) {
                window.groupConditionalsData = payload.conditionals;
                if (payload.conditionals.rules && payload.conditionals.rules.length > 0 && typeof window.populateConditionalRules === 'function') {
                    window.populateConditionalRules(payload.conditionals);
                }
            }
            return payload.field_groups;
        }
    } catch (error) {
        console.error('Unable to fetch modifier fields', error);
    }

    return [];
}

function populateFieldGroups(fieldGroups, options = {}) {
    if (!Array.isArray(fieldGroups)) {
        return;
    }

    fieldGroups
        .slice()
        .sort((a, b) => Number(a?.display_order ?? 0) - Number(b?.display_order ?? 0))
        .forEach(group => {
        addNewFieldGroup(group, options);
        });

    refreshFieldGroupOrderUI();
    processPendingFieldConditions();
    updateFieldConditionAvailability();
}

function normalizeModifiersToFieldGroups(modifiersData) {
    if (!Array.isArray(modifiersData) || modifiersData.length === 0) {
        return [];
    }

    // Create a field group for each modifier
    return modifiersData.map(modifier => {
        const displayType = modifier.type || modifier.display_type || 'radio';
        const rawOptions = modifier.options;
        const attributes = modifier.attributes || {};

        const maxSelections = displayType === 'radio'
            ? 1
            : (Array.isArray(rawOptions) && rawOptions.length > 0
                ? rawOptions.length
                : (typeof modifier.max_choice === 'number' ? modifier.max_choice : 1));

        let individualModifiers = [];
        if (Array.isArray(rawOptions)) {
            individualModifiers = rawOptions.map(option => ({
                id: option.id || Helpers.uniqueId(),
                name: option.label || option.name || '',
                price: option.price !== undefined ? option.price : (modifier.price_adjustment || 0),
                price_type: normalizePriceTypeValue(option.price_type || modifier.price_type),
                included: isTruthy(option.included ?? option.selected ?? modifier.is_default),
                description: option.description || modifier.description || ''
            }));
        } else if (rawOptions && typeof rawOptions === 'object' && displayType !== 'text') {
            individualModifiers = [{
                id: rawOptions.id || modifier.id || Helpers.uniqueId(),
                name: rawOptions.label || rawOptions.name || modifier.name || '',
                price: rawOptions.price !== undefined ? rawOptions.price : (modifier.price_adjustment || 0),
                price_type: normalizePriceTypeValue(rawOptions.price_type || modifier.price_type),
                included: isTruthy(rawOptions.included ?? rawOptions.selected ?? modifier.is_default),
                description: rawOptions.description || modifier.description || ''
            }];
        }

        const cartOptions = modifier.cart_options || {};
        const checkoutOptions = modifier.checkout || {};
        const orderData = modifier.order_data || {};

        const fieldGroup = {
            field_id: modifier.field_id || null,  // Preserve field_id from database
            label: modifier.label || modifier.name || 'Untitled Field',
            display_type: displayType,
            instructions: modifier.instructions || '',
            is_required: isTruthy(modifier.is_required),
            options: individualModifiers,
            min_selections: modifier.min_choice || 0,
            max_selections: maxSelections,
            quantity_based: isTruthy(modifier.is_qty),
            hide_on_cart: isTruthy(cartOptions.hideOnCart),
            hide_on_checkout: checkoutOptions.display === false,
            hide_on_order: orderData.printOnReceipt === false,
            wrapper_class: attributes?.cssClass ?? attributes.cssClass ?? '',
            wrapper_width: attributes?.width ?? attributes.width ?? ''
        };

        if (displayType === 'text') {
            const textOptionSource = Array.isArray(rawOptions)
                ? (rawOptions.length > 0 ? rawOptions[0] : null)
                : (rawOptions && typeof rawOptions === 'object' ? rawOptions : null);

            const textConfig = {
                ...attributes,
                ...(textOptionSource || {})
            };

            const placeholder = textConfig.placeholder ?? modifier.placeholder ?? '';
            const minLengthRaw = textConfig.min_length ?? modifier.min_length ?? 0;
            const maxLengthRaw = textConfig.max_length ?? modifier.max_length;
            const regexPattern = textConfig.regex_pattern ?? modifier.regex_pattern ?? '';
            const inputType = textConfig.input_type ?? modifier.input_type ?? 'text';
            const adjustPricingValue = textConfig.adjust_pricing ?? modifier.adjust_pricing;
            const priceTypeValue = normalizePriceTypeValue(textConfig.price_type ?? modifier.price_type);
            const priceValueRaw = textConfig.price ?? modifier.price ?? modifier.price_adjustment ?? 0;

            const parsedMinLength = parseInt(minLengthRaw, 10);
            const parsedMaxLength = maxLengthRaw !== undefined && maxLengthRaw !== null
                ? parseInt(maxLengthRaw, 10)
                : '';
            const parsedPrice = typeof priceValueRaw === 'string' ? parseFloat(priceValueRaw) : priceValueRaw;

            fieldGroup.placeholder = placeholder;
            fieldGroup.input_type = inputType;
            fieldGroup.min_length = Number.isFinite(parsedMinLength) ? parsedMinLength : 0;
            fieldGroup.max_length = Number.isFinite(parsedMaxLength) ? parsedMaxLength : '';
            fieldGroup.regex_pattern = regexPattern;
            fieldGroup.adjust_pricing = isTruthy(adjustPricingValue);
            fieldGroup.price_type = priceTypeValue;
            fieldGroup.price = Number.isFinite(parsedPrice) ? parsedPrice : 0;
            fieldGroup.options = [];
        }

        // Extract field-level conditions from conditionals column
        if (modifier.conditionals && typeof modifier.conditionals === 'object') {
            if (Array.isArray(modifier.conditionals.rule_groups) && modifier.conditionals.rule_groups.length > 0) {
                // Transform rule_groups structure to flat conditions array
                const conditions = [];
                modifier.conditionals.rule_groups.forEach(group => {
                    if (Array.isArray(group.rules)) {
                        group.rules.forEach(rule => {
                            conditions.push({
                                group_id: group.id,
                                target_field_id: rule.subject,  // Map 'subject' to 'target_field_id'
                                operator: rule.condition,       // Map 'condition' to 'operator'
                                value: rule.value,
                                value_label: rule.value_label,
                                values: rule.values || []
                            });
                        });
                    }
                });
                if (conditions.length > 0) {
                    fieldGroup.conditions = conditions;
                }
            }
        }

        return fieldGroup;
    });
}


function normalizeDisplayTypeValue(value) {
    if (!value) {
        return 'radio';
    }

    const normalized = String(value).toLowerCase();
    if (normalized === 'checkbox') {
        return 'checkboxes';
    }
    if (normalized === 'select') {
        return 'dropdown';
    }
    return normalized;
}

function normalizePriceTypeValue(value) {
    if (value === undefined || value === null) {
        return 'none';
    }

    const numericMapping = {
        1: 'fixed',
        2: 'percentage',
        3: 'none',
        4: 'included',
        5: 'none'
    };

    if (typeof value === 'number') {
        return numericMapping[value] || 'none';
    }

    const normalized = String(value).toLowerCase();
    switch (normalized) {
        case 'fixed':
        case 'percentage':
        case 'included':
        case 'qt':
            return normalized;
        case 'no_change':
        case 'free':
        case 'none':
            return 'none';
        case 'quantity':
        case 'quantity_based':
            return 'qt';
        default:
            return 'none';
    }
}

function formatDisplayTypeLabel(value) {
    switch (normalizeDisplayTypeValue(value)) {
        case 'checkboxes':
            return 'Checkboxes';
        case 'dropdown':
            return 'Select';
        case 'text':
            return 'Text';
        case 'radio':
        default:
            return 'Radio';
    }
}

/**
 * Toggle collapsible sections
 */
function toggleSection(sectionName) {
    const content = document.getElementById(sectionName + '-content');
    const chevron = document.getElementById(sectionName + '-chevron');

    if (content.style.display === 'none' || !content.style.display) {
        content.style.display = 'block';
        chevron.style.transform = 'rotate(180deg)';
    } else {
        content.style.display = 'none';
        chevron.style.transform = 'rotate(0deg)';
    }
}

/**
 * Toggle field group content visibility
 */
function toggleFieldGroupContent(headerElement) {
    // Find the next sibling element with the field-group-body class
    const content = headerElement.nextElementSibling;

    if (content && content.classList.contains('field-group-body')) {
        if (content.style.display === 'none') {
            content.style.display = 'block';
        } else {
            content.style.display = 'none';
        }
    }
}

/**
 * Add a new field group
 */
function addNewFieldGroup(fieldGroupData = null, options = {}) {
    fieldGroupCounter++;
    optionCounters[fieldGroupCounter] = 0;

    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer) return;

    // Generate unique ID for this field group
    const fieldGroupId = fieldGroupData?.field_id || Helpers.uniqueId();
    const { skipAutoUpdates = false, startCollapsed } = options;
    const displayType = normalizeDisplayTypeValue(fieldGroupData?.display_type || 'radio');
    const displayTypeLabel = formatDisplayTypeLabel(displayType);
    const groupLabel = fieldGroupData?.label?.trim() || 'New Field';
    const instructionsValue = fieldGroupData?.instructions || '';

    // Debug logging for instructions
    if (fieldGroupData) {
        console.log(`Field group data for "${groupLabel}":`, {
            hasInstructions: 'instructions' in fieldGroupData,
            instructionsValue: instructionsValue,
            instructionsRaw: fieldGroupData.instructions
        });
    }
    const placeholderValue = displayType === 'text'
        ? (fieldGroupData?.placeholder ?? fieldGroupData?.options?.[0]?.placeholder ?? '')
        : '';
    const minSelectionsValue = typeof fieldGroupData?.min_selections === 'number' ? fieldGroupData.min_selections : 0;
    const maxSelectionsRaw = typeof fieldGroupData?.max_selections === 'number' ? fieldGroupData.max_selections : 0;
    const maxSelectionsValue = Math.max(0, maxSelectionsRaw);

    const shouldStartCollapsed = startCollapsed !== undefined ? startCollapsed : Boolean(fieldGroupData);
    const initialBodyDisplay = shouldStartCollapsed ? 'none' : 'block';
    const initialDisplayOrder = Number(fieldGroupData?.display_order ?? fieldsContainer.querySelectorAll('[id^="field-group-"]').length);

    const fieldGroup = document.createElement('div');
    fieldGroup.className = 'mb-4 border border-gray-200';
    fieldGroup.id = `field-group-${fieldGroupCounter}`;
    fieldGroup.setAttribute('data-field-id', fieldGroupId);
    fieldGroup.dataset.fieldKey = fieldGroupId;
    fieldGroup.dataset.displayOrder = String(initialDisplayOrder);
    fieldConditions[String(fieldGroupCounter)] = [];
    fieldConditionGroupCounters[String(fieldGroupCounter)] = 0;
    if (Array.isArray(fieldGroupData?.conditions) && fieldGroupData.conditions.length > 0) {
        pendingFieldConditions.push({
            fieldGroupNumericId: fieldGroupCounter,
            conditions: fieldGroupData.conditions
        });
    }

    fieldGroup.innerHTML = `
        <!-- Field group header -->
        <div class="field-group-header flex items-center justify-between bg-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-300 transition-colors" draggable="true" onclick="toggleFieldGroupContent(this)">
            <div class="flex items-center gap-3">
                <button
                    type="button"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                    data-drag-handle="true"
                    title="Drag to reorder field"
                    aria-label="Drag to reorder field"
                    onclick="event.stopPropagation()"
                >
                    <i class="uil uil-draggabledots text-base"></i>
                </button>
                <div>
                <p class="text-sm font-semibold text-gray-800 field-group-name">${groupLabel}</p>
                <p class="text-[11px] text-gray-500 field-group-description">${displayTypeLabel} • Field ${fieldGroupCounter} • ID: ${fieldGroupId}</p>
                </div>
            </div>
            <div class="flex items-center gap-2">
                <button type="button" class="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50" onclick="event.stopPropagation(); duplicateFieldGroup(${fieldGroupCounter})">Duplicate</button>
                <button type="button" class="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50" onclick="event.stopPropagation(); removeFieldGroup(${fieldGroupCounter})">Delete</button>
            </div>
        </div>

        <!-- Field group body -->
        <div class="divide-y divide-gray-200 field-group-body" style="display: ${initialBodyDisplay};">
            <!-- Hidden field for the unique field ID -->
            <input type="hidden" name="field_groups[${fieldGroupCounter}][field_id]" value="${fieldGroupId}" />
            <input type="hidden" name="field_groups[${fieldGroupCounter}][display_order]" value="${initialDisplayOrder}" />

            <!-- Type Section -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Type</div>
                <div class="col-span-9 px-4 py-3">
                    <select name="field_groups[${fieldGroupCounter}][display_type]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" onchange="handleFieldTypeChange(${fieldGroupCounter})">
                        <option value="radio"${displayType === 'radio' ? ' selected' : ''}>Radio</option>
                        <option value="checkboxes"${displayType === 'checkboxes' ? ' selected' : ''}>Checkboxes</option>
                        <option value="dropdown"${displayType === 'dropdown' ? ' selected' : ''}>Select</option>
                        <option value="text"${displayType === 'text' ? ' selected' : ''}>Text</option>
                    </select>
                    <p class="mt-1 text-xs text-gray-500">Display style will be automatically set based on group type.</p>
                </div>
            </div>

            <!-- Label -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Label</div>
                <div class="col-span-9 px-4 py-3">
                    <input type="text" name="field_groups[${fieldGroupCounter}][label]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="e.g., Add Ons, Extras, Toppings" onchange="updateFieldGroupName(${fieldGroupCounter}, this.value)" />
                </div>
            </div>

            <!-- Instructions -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Instructions</div>
                <div class="col-span-9 px-4 py-3">
                    <textarea name="field_groups[${fieldGroupCounter}][instructions]" rows="3" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="Optional instructions for customers"></textarea>
                </div>
            </div>

            <!-- Required -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Required</div>
                <div class="col-span-9 px-4 py-3">
                    <label class="inline-flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="field_groups[${fieldGroupCounter}][is_required]" value="1" class="sr-only toggle-input" />
                        <div class="relative">
                            <div class="toggle-track">
                                <div class="toggle-thumb"></div>
                            </div>
                        </div>
                        <span class="text-sm text-gray-600">No</span>
                    </label>
                </div>
            </div>

            <!-- Options -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Options</div>
                <div class="col-span-9 px-4 py-3">
                    <!-- Options table header -->
                    <div class="mb-2 flex items-center gap-3 text-[11px] font-medium text-gray-500">
                        <span class="flex-1">Option label</span>
                        <span class="flex-1">Adjust pricing</span>
                        <span class="flex-1">Pricing amount</span>
                        <div class="flex items-center gap-4 ml-auto">
                            <span class="w-16 text-center">Included</span>
                            <span class="w-16 text-center">Selected</span>
                            <span class="w-8 text-center"></span>
                        </div>
                    </div>

                    <!-- Options Container -->
                    <div id="options-container-${fieldGroupCounter}" class="space-y-2">
                        <!-- Dynamic options will be loaded here -->
                    </div>

                    <div class="mt-3 flex items-center justify-between">
                        <button type="button" class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50" onclick="addNewOption(${fieldGroupCounter})">Add option</button>
                        <a href="#" class="text-xs text-sky-600 hover:underline">Help with pricing</a>
                    </div>
                </div>
            </div>

            <!-- Text Input Settings (shown only for text type) -->
            <div id="text-settings-${fieldGroupCounter}" class="grid grid-cols-12" style="display: none;">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Text Settings</div>
                <div class="col-span-9 px-4 py-3 space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Placeholder Text</label>
                        <input type="text" name="field_groups[${fieldGroupCounter}][placeholder]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="Enter placeholder text for customers" />
                        <p class="mt-1 text-xs text-gray-500">Text that appears in the input field before customers start typing</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Input Type</label>
                        <select name="field_groups[${fieldGroupCounter}][input_type]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30">
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="email">Email</option>
                            <option value="tel">Phone</option>
                            <option value="number">Number</option>
                        </select>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Minimum Length</label>
                            <input type="number" name="field_groups[${fieldGroupCounter}][min_length]" min="0" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="0" />
                            <p class="mt-1 text-xs text-gray-500">Minimum number of characters required</p>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Maximum Length</label>
                            <input type="number" name="field_groups[${fieldGroupCounter}][max_length]" min="1" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="255" />
                            <p class="mt-1 text-xs text-gray-500">Maximum number of characters allowed</p>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Regex Validation</label>
                        <input type="text" name="field_groups[${fieldGroupCounter}][regex_pattern]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" placeholder="^[A-Za-z0-9]+$" />
                        <p class="mt-1 text-xs text-gray-500">Regular expression pattern for validation (optional)</p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Adjust Pricing</label>
                        <label class="inline-flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" name="field_groups[${fieldGroupCounter}][adjust_pricing]" value="1" class="sr-only toggle-input" onchange="toggleTextPricingFields(${fieldGroupCounter})" />
                            <div class="relative">
                                <div class="toggle-track">
                                    <div class="toggle-thumb"></div>
                                </div>
                            </div>
                            <span class="text-sm text-gray-600">No</span>
                        </label>
                        <p class="mt-1 text-xs text-gray-500">Should the price of the product or cart change when the user interacts with this field?</p>
                    </div>

                    <!-- Pricing Settings (shown only when Adjust Pricing is enabled) -->
                    <div id="text-pricing-fields-${fieldGroupCounter}" class="space-y-3" style="display: none;">
                        <div class="border-t border-gray-200 pt-3">
                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Pricing Type</label>
                                    <select name="field_groups[${fieldGroupCounter}][price_type]" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" onchange="updateTextPricingSymbol(this, ${fieldGroupCounter})">
                                        <option value="none">No price change</option>
                                        <option value="fixed">Flat fee</option>
                                        <option value="qt">Quantity based flat fee</option>
                                        <option value="p">Percentage based fee</option>
                                        <option value="percent">Quantity based percentage fee</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Pricing Amount</label>
                                    <div class="flex">
                                        <span id="text-price-symbol-${fieldGroupCounter}" class="inline-flex items-center px-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md">$</span>
                                        <input type="number" name="field_groups[${fieldGroupCounter}][price]" value="0" step="0.01" min="0"
                                               class="flex-1 text-sm border border-l-0 border-gray-300 rounded-r-md px-2 py-1.5 focus:border-sky-500 focus:ring-1 focus:ring-sky-500" placeholder="0.00">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Minimum choices needed -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Minimum choices needed</div>
                <div class="col-span-9 px-4 py-3">
                    <input type="number" name="field_groups[${fieldGroupCounter}][min_selections]" value="0" min="0" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" />
                </div>
            </div>

            <!-- Maximum choices allowed -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Maximum choices allowed</div>
                <div class="col-span-9 px-4 py-3">
                    <input type="number" name="field_groups[${fieldGroupCounter}][max_selections]" value="0" min="0" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30" />
                    <p class="mt-1 text-xs text-gray-500">Automatically set based on number of options, but you can adjust if needed.</p>
                </div>
            </div>

            <!-- Quantity based -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Quantity based</div>
                <div class="col-span-9 px-4 py-3">
                    <label class="inline-flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="field_groups[${fieldGroupCounter}][quantity_based]" value="1" class="sr-only toggle-input" />
                        <div class="relative">
                            <div class="toggle-track">
                                <div class="toggle-thumb"></div>
                            </div>
                        </div>
                        <span class="text-sm text-gray-600">No</span>
                    </label>
                    <p class="mt-1 text-xs text-gray-500">Should this field appear multiple times depending on the chosen quantity?</p>
                </div>
            </div>

            <!-- Conditionals -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Conditionals</div>
                <div class="col-span-9 px-4 py-3">
                    <p class="text-sm text-gray-500" id="field-condition-helper-${fieldGroupCounter}" data-default-message="You need at least 2 fields to create conditional rules. Add another field first.">
                        You need at least 2 fields to create conditional rules. Add another field first.
                    </p>
                    <div class="mt-3">
                        <button type="button" id="add-condition-btn-${fieldGroupCounter}" onclick="addFieldCondition(${fieldGroupCounter})" class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50" disabled>
                            Add Conditional Rule
                        </button>
                    </div>
                    <!-- Field-specific conditions container -->
                    <div id="field-conditions-container-${fieldGroupCounter}" class="mt-4 space-y-3">
                        <!-- Dynamic field conditions will be added here -->
                    </div>
                    <div class="mt-4 flex justify-end">
                        <button type="button" id="add-condition-group-btn-${fieldGroupCounter}" class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 disabled:opacity-50" onclick="addFieldConditionGroup(${fieldGroupCounter})" disabled>
                            Or
                        </button>
                    </div>
                </div>
            </div>

            <!-- Hide on cart, checkout, order -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Hide on cart, checkout, order</div>
                <div class="col-span-9 px-4 py-3 space-y-3">
                    <label class="inline-flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="field_groups[${fieldGroupCounter}][hide_on_cart]" value="1" class="sr-only toggle-input" />
                        <div class="relative">
                            <div class="toggle-track">
                                <div class="toggle-thumb"></div>
                            </div>
                        </div>
                        <span class="text-sm text-gray-600">No</span>
                        <span class="text-sm text-gray-700 ml-4">Hide this field & value on the cart page</span>
                    </label>

                    <label class="inline-flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="field_groups[${fieldGroupCounter}][hide_on_checkout]" value="1" class="sr-only toggle-input" />
                        <div class="relative">
                            <div class="toggle-track">
                                <div class="toggle-thumb"></div>
                            </div>
                        </div>
                        <span class="text-sm text-gray-600">No</span>
                        <span class="text-sm text-gray-700 ml-4">Hide this field & value on the checkout page</span>
                    </label>

                    <label class="inline-flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" name="field_groups[${fieldGroupCounter}][hide_on_order]" value="1" class="sr-only toggle-input" />
                        <div class="relative">
                            <div class="toggle-track">
                                <div class="toggle-thumb"></div>
                            </div>
                        </div>
                        <span class="text-sm text-gray-600">No</span>
                        <span class="text-sm text-gray-700 ml-4">Hide this field & value on the "order received" page and emails.</span>
                    </label>
                </div>
            </div>

            <!-- Wrapper attributes -->
            <div class="grid grid-cols-12">
                <div class="col-span-3 border-r border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">Wrapper attributes</div>
                <div class="col-span-9 px-4 py-3">
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Width</label>
                            <div class="flex">
                                <input type="number" name="field_groups[${fieldGroupCounter}][wrapper_width]" class="flex-1 rounded-l-md border border-r-0 border-gray-300 px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500" placeholder="100">
                                <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-r-md">%</span>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Class</label>
                            <input type="text" name="field_groups[${fieldGroupCounter}][wrapper_class]" class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500" placeholder="css-class-name">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    fieldsContainer.appendChild(fieldGroup);

    const fieldPrefix = `field_groups[${fieldGroupCounter}]`;

    const labelInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[label]"]`);
    if (labelInput && fieldGroupData?.label) {
        labelInput.value = fieldGroupData.label;
        updateFieldGroupName(fieldGroupCounter, fieldGroupData.label);
    }

    // Populate placeholder for text fields (same pattern as label)
    if (displayType === 'text' && fieldGroupData?.options?.[0]?.placeholder) {
        const placeholderInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[placeholder]"]`);
        if (placeholderInput) {
            placeholderInput.value = fieldGroupData.options[0].placeholder;
        }
    }

    const instructionsInput = fieldGroup.querySelector(`textarea[name="${fieldPrefix}[instructions]"]`);
    if (instructionsInput) {
        console.log(`Setting instructions for field ${fieldGroupCounter}:`, instructionsValue);
        instructionsInput.value = instructionsValue;
    } else {
        console.warn(`Instructions textarea not found for field ${fieldGroupCounter}, selector: textarea[name="${fieldPrefix}[instructions]"]`);
    }

    const displayTypeSelect = fieldGroup.querySelector(`select[name="${fieldPrefix}[display_type]"]`);
    if (displayTypeSelect) {
        displayTypeSelect.value = displayType;
        // Handle field type change to show/hide appropriate sections
        handleFieldTypeChange(fieldGroupCounter);
    }

    // If this is a text field, populate text-specific settings
    if (displayType === 'text' && fieldGroupData?.options && Array.isArray(fieldGroupData.options) && fieldGroupData.options.length > 0) {
        const textData = fieldGroupData.options[0]; // Get text settings from first option object

        // Placeholder is populated in main flow above

        // Populate input type
        const inputTypeSelect = fieldGroup.querySelector(`select[name="${fieldPrefix}[input_type]"]`);
        if (inputTypeSelect && textData.input_type) {
            inputTypeSelect.value = textData.input_type;
        }

        // Populate min/max length
        const minLengthInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[min_length]"]`);
        if (minLengthInput && textData.min_length !== undefined) {
            minLengthInput.value = textData.min_length;
        }

        const maxLengthInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[max_length]"]`);
        if (maxLengthInput && textData.max_length !== undefined) {
            maxLengthInput.value = textData.max_length;
        }

        // Populate regex pattern
        const regexInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[regex_pattern]"]`);
        if (regexInput && textData.regex_pattern) {
            regexInput.value = textData.regex_pattern;
        }

        // Populate adjust pricing checkbox
        const adjustPricingCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[adjust_pricing]"]`);
        if (adjustPricingCheckbox) {
            adjustPricingCheckbox.checked = isTruthy(textData.adjust_pricing);
            applyToggleState(adjustPricingCheckbox);

            // If adjust pricing is enabled, show pricing fields and populate them
            if (textData.adjust_pricing) {
                setTimeout(() => {
                    console.log('Showing pricing fields for field group:', fieldGroupCounter);
                    toggleTextPricingFields(fieldGroupCounter);

                    // Populate price type
                    const priceTypeSelect = fieldGroup.querySelector(`select[name="${fieldPrefix}[price_type]"]`);
                    if (priceTypeSelect && textData.price_type) {
                        priceTypeSelect.value = textData.price_type;
                    }

                    // Populate price
                    const priceInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[price]"]`);
                    if (priceInput && textData.price !== undefined) {
                        priceInput.value = textData.price;
                    }
                }, 100);
            }
        }
    }

    const minSelectionsInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[min_selections]"]`);
    if (minSelectionsInput) {
        minSelectionsInput.value = minSelectionsValue;
    }

    const maxSelectionsInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[max_selections]"]`);
    if (maxSelectionsInput) {
        maxSelectionsInput.value = maxSelectionsValue;
    }

    const isRequiredCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[is_required]"]`);
    if (isRequiredCheckbox) {
        isRequiredCheckbox.checked = isTruthy(fieldGroupData?.is_required);
        applyToggleState(isRequiredCheckbox);
    }

    const quantityBasedCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[quantity_based]"]`);
    if (quantityBasedCheckbox) {
        quantityBasedCheckbox.checked = isTruthy(fieldGroupData?.quantity_based);
        applyToggleState(quantityBasedCheckbox);
    }

    const hideOnCartCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[hide_on_cart]"]`);
    if (hideOnCartCheckbox) {
        hideOnCartCheckbox.checked = isTruthy(fieldGroupData?.hide_on_cart);
        applyToggleState(hideOnCartCheckbox);
    }

    const hideOnCheckoutCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[hide_on_checkout]"]`);
    if (hideOnCheckoutCheckbox) {
        hideOnCheckoutCheckbox.checked = isTruthy(fieldGroupData?.hide_on_checkout);
        applyToggleState(hideOnCheckoutCheckbox);
    }

    const hideOnOrderCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[hide_on_order]"]`);
    if (hideOnOrderCheckbox) {
        hideOnOrderCheckbox.checked = isTruthy(fieldGroupData?.hide_on_order);
        applyToggleState(hideOnOrderCheckbox);
    }

    // Populate wrapper attributes
    const wrapperWidthInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[wrapper_width]"]`);
    if (wrapperWidthInput && fieldGroupData && 'wrapper_width' in fieldGroupData) {
        wrapperWidthInput.value = fieldGroupData.wrapper_width;
    }

    const wrapperClassInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[wrapper_class]"]`);
    if (wrapperClassInput && fieldGroupData && 'wrapper_class' in fieldGroupData) {
        wrapperClassInput.value = fieldGroupData.wrapper_class;
    }

    // Populate text field settings if this is a text field
    if (fieldGroupData) {
        console.log('Populating field group data:', fieldGroupData);
        console.log('Field prefix:', fieldPrefix);

        const placeholderInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[placeholder]"]`);
        if (placeholderInput) {
            console.log('Setting placeholder value:', fieldGroupData.placeholder);
            placeholderInput.value = fieldGroupData.placeholder || '';
        } else {
            console.log('Placeholder input not found');
        }

        const inputTypeSelect = fieldGroup.querySelector(`select[name="${fieldPrefix}[input_type]"]`);
        if (inputTypeSelect) {
            inputTypeSelect.value = fieldGroupData.input_type || 'text';
        }

        const minLengthInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[min_length]"]`);
        if (minLengthInput) {
            minLengthInput.value = fieldGroupData.min_length || 0;
        }

        const maxLengthInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[max_length]"]`);
        if (maxLengthInput) {
            maxLengthInput.value = fieldGroupData.max_length || '';
        }

        const regexPatternInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[regex_pattern]"]`);
        if (regexPatternInput) {
            regexPatternInput.value = fieldGroupData.regex_pattern || '';
        }

        const adjustPricingCheckbox = fieldGroup.querySelector(`input[name="${fieldPrefix}[adjust_pricing]"]`);
        if (adjustPricingCheckbox) {
            console.log('Setting adjust_pricing:', fieldGroupData.adjust_pricing);
            adjustPricingCheckbox.checked = isTruthy(fieldGroupData.adjust_pricing);
            applyToggleState(adjustPricingCheckbox);
            // Show/hide pricing fields based on state
            setTimeout(() => toggleTextPricingFields(fieldGroupCounter), 100);
        } else {
            console.log('Adjust pricing checkbox not found');
        }

        const priceTypeSelect = fieldGroup.querySelector(`select[name="${fieldPrefix}[price_type]"]`);
        if (priceTypeSelect) {
            priceTypeSelect.value = fieldGroupData.price_type || 'none';
            // Update pricing symbol
            updateTextPricingSymbol(priceTypeSelect, fieldGroupCounter);
        }

        const priceInput = fieldGroup.querySelector(`input[name="${fieldPrefix}[price]"]`);
        if (priceInput) {
            console.log('Setting price value:', fieldGroupData.price);
            priceInput.value = fieldGroupData.price || 0;
        } else {
            console.log('Price input not found');
        }
    }

    const modifiers = displayType === 'text'
        ? []
        : (Array.isArray(fieldGroupData?.options) ? fieldGroupData.options : []);

    if (displayType !== 'text') {
        if (modifiers.length > 0) {
            modifiers.forEach(modifier => addNewOption(fieldGroupCounter, modifier, { skipAutoUpdate: skipAutoUpdates }));
        } else {
            addNewOption(fieldGroupCounter, null, { skipAutoUpdate: false });
        }
    }

    updateFieldGroupDescription(fieldGroupCounter);

    updateFieldConditionAvailability();

    if (!skipAutoUpdates) {
        refreshFieldGroupOrderUI();
        updateOverallGroupType();
    }
}

/**
 * Remove a field group
 */
function removeFieldGroup(fieldGroupId) {
    const fieldGroup = document.getElementById(`field-group-${fieldGroupId}`);
    const removedFieldKey = fieldGroup?.dataset.fieldKey;
    if (fieldGroup) {
        fieldGroup.remove();
        delete optionCounters[fieldGroupId];
        updateOverallGroupType();
        refreshFieldGroupOrderUI();
    }
    delete fieldConditions[String(fieldGroupId)];
    delete fieldConditionGroupCounters[String(fieldGroupId)];
    if (removedFieldKey) {
        removeFieldConditionsReferencingField(removedFieldKey);
    }
    updateFieldConditionAvailability();
}

/**
 * Duplicate a field group (stub - full implementation below)
 */

/**
 * Update field group name in header
 */
function updateFieldGroupName(fieldGroupId, newName) {
    const nameElement = document.querySelector(`#field-group-${fieldGroupId} .field-group-name`);
    if (nameElement && newName.trim()) {
        nameElement.textContent = newName.trim();
    }
    updateFieldConditionAvailability();
}

/**
 * Update field group description in header
 */
function updateFieldGroupDescription(fieldGroupId) {
    const select = document.querySelector(`select[name="field_groups[${fieldGroupId}][display_type]"]`);
    const descElement = document.querySelector(`#field-group-${fieldGroupId} .field-group-description`);
    const fieldGroupElement = document.querySelector(`#field-group-${fieldGroupId}`);
    const fieldsContainer = document.getElementById('fields-container');

    if (select && descElement && fieldGroupElement && fieldsContainer) {
        const visualOrder = Array.from(fieldsContainer.querySelectorAll('[id^="field-group-"]')).indexOf(fieldGroupElement) + 1;
        const uniqueId = fieldGroupElement.getAttribute('data-field-id');
        descElement.textContent = `${formatDisplayTypeLabel(select.value)} • Field ${visualOrder} • ID: ${uniqueId}`;
    }
}

function initializeFieldGroupSorting() {
    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer || fieldsContainer.dataset.sortableInitialized === '1') {
        return;
    }

    fieldsContainer.dataset.sortableInitialized = '1';

    fieldsContainer.addEventListener('dragstart', (event) => {
        const header = event.target.closest('.field-group-header');
        const handle = event.target.closest('[data-drag-handle]');
        if (!header || !handle) {
            event.preventDefault();
            return;
        }

        const fieldGroup = header.closest('[id^="field-group-"]');
        if (!fieldGroup) {
            event.preventDefault();
            return;
        }

        draggedFieldGroupId = fieldGroup.id;
        fieldGroup.classList.add('opacity-60');

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', fieldGroup.id);
        }
    });

    fieldsContainer.addEventListener('dragover', (event) => {
        const targetGroup = getFieldGroupFromEventTarget(event.target);
        const draggedGroup = draggedFieldGroupId ? document.getElementById(draggedFieldGroupId) : null;
        if (!targetGroup || !draggedGroup || targetGroup === draggedGroup) {
            return;
        }

        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }

        clearFieldGroupDropIndicators();
        const insertAfter = shouldInsertAfterTarget(event, targetGroup);
        targetGroup.classList.add('ring-2', insertAfter ? 'ring-green-400' : 'ring-sky-400');
    });

    fieldsContainer.addEventListener('drop', (event) => {
        const targetGroup = getFieldGroupFromEventTarget(event.target);
        const draggedGroup = draggedFieldGroupId ? document.getElementById(draggedFieldGroupId) : null;
        if (!targetGroup || !draggedGroup || targetGroup === draggedGroup) {
            return;
        }

        event.preventDefault();

        if (shouldInsertAfterTarget(event, targetGroup)) {
            targetGroup.insertAdjacentElement('afterend', draggedGroup);
        } else {
            targetGroup.insertAdjacentElement('beforebegin', draggedGroup);
        }

        clearFieldGroupDropIndicators();
        refreshFieldGroupOrderUI();
        updateFieldConditionAvailability();
    });

    fieldsContainer.addEventListener('dragend', () => {
        const draggedGroup = draggedFieldGroupId ? document.getElementById(draggedFieldGroupId) : null;
        if (draggedGroup) {
            draggedGroup.classList.remove('opacity-60');
        }
        draggedFieldGroupId = null;
        clearFieldGroupDropIndicators();
    });
}

function getFieldGroupFromEventTarget(target) {
    const fieldGroup = target instanceof Element ? target.closest('[id^="field-group-"]') : null;
    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldGroup || !fieldsContainer || !fieldsContainer.contains(fieldGroup)) {
        return null;
    }

    return fieldGroup;
}

function shouldInsertAfterTarget(event, targetGroup) {
    const rect = targetGroup.getBoundingClientRect();
    return event.clientY > rect.top + (rect.height / 2);
}

function clearFieldGroupDropIndicators() {
    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer) {
        return;
    }

    fieldsContainer.querySelectorAll('[id^="field-group-"]').forEach((group) => {
        group.classList.remove('ring-2', 'ring-sky-400', 'ring-green-400', 'opacity-60');
    });
}

function refreshFieldGroupOrderUI() {
    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer) {
        return;
    }

    fieldsContainer.querySelectorAll('[id^="field-group-"]').forEach((fieldGroup, index) => {
        fieldGroup.dataset.displayOrder = String(index);
        const numericId = fieldGroup.id.replace('field-group-', '');
        const displayOrderInput = fieldGroup.querySelector(`input[name="field_groups[${numericId}][display_order]"]`);
        if (displayOrderInput) {
            displayOrderInput.value = String(index);
        }
        updateFieldGroupDescription(numericId);
    });
}

/**
 * Handle field type change - show/hide appropriate sections
 */
function handleFieldTypeChange(fieldGroupId) {
    const select = document.querySelector(`select[name="field_groups[${fieldGroupId}][display_type]"]`);
    const fieldGroup = document.getElementById(`field-group-${fieldGroupId}`);

    if (!select || !fieldGroup) return;

    const selectedType = select.value;

    // Find sections within this specific field group
    const optionsContainer = document.getElementById(`options-container-${fieldGroupId}`);
    const optionsSection = optionsContainer?.closest('.grid.grid-cols-12');
    const textSettings = document.getElementById(`text-settings-${fieldGroupId}`);
    const minChoicesSection = fieldGroup.querySelector(`input[name*="min_selections"]`)?.closest('.grid.grid-cols-12');
    const maxChoicesSection = fieldGroup.querySelector(`input[name*="max_selections"]`)?.closest('.grid.grid-cols-12');

    // Find all option inputs in this field group (not just name inputs)
    const allOptionInputs = fieldGroup.querySelectorAll(`input[name*="field_groups[${fieldGroupId}][modifiers]"], select[name*="field_groups[${fieldGroupId}][modifiers]"]`);

    if (selectedType === 'text') {
        // Completely hide/remove options section and choice-related sections
        if (optionsSection) {
            optionsSection.style.display = 'none';
            optionsSection.setAttribute('data-hidden-for-text', 'true');
        }
        if (minChoicesSection) {
            minChoicesSection.style.display = 'none';
            minChoicesSection.setAttribute('data-hidden-for-text', 'true');
        }
        if (maxChoicesSection) {
            maxChoicesSection.style.display = 'none';
            maxChoicesSection.setAttribute('data-hidden-for-text', 'true');
        }

        // Disable all option inputs so they don't get submitted
        allOptionInputs.forEach(input => {
            input.disabled = true;
            input.setAttribute('data-was-disabled-for-text', 'true');
            if (input.hasAttribute('required')) {
                input.removeAttribute('required');
                input.setAttribute('data-was-required', 'true');
            }
        });

        // Show text settings
        if (textSettings) textSettings.style.display = 'grid';
    } else {
        // Show options section and choice-related sections that were hidden for text
        if (optionsSection && optionsSection.getAttribute('data-hidden-for-text') === 'true') {
            optionsSection.style.display = 'grid';
            optionsSection.removeAttribute('data-hidden-for-text');
        }
        if (minChoicesSection && minChoicesSection.getAttribute('data-hidden-for-text') === 'true') {
            minChoicesSection.style.display = 'grid';
            minChoicesSection.removeAttribute('data-hidden-for-text');
        }
        if (maxChoicesSection && maxChoicesSection.getAttribute('data-hidden-for-text') === 'true') {
            maxChoicesSection.style.display = 'grid';
            maxChoicesSection.removeAttribute('data-hidden-for-text');
        }

        // Re-enable all option inputs
        allOptionInputs.forEach(input => {
            if (input.getAttribute('data-was-disabled-for-text') === 'true') {
                input.disabled = false;
                input.removeAttribute('data-was-disabled-for-text');
                if (input.getAttribute('data-was-required') === 'true') {
                    input.setAttribute('required', '');
                    input.removeAttribute('data-was-required');
                }
            }
        });

        // Hide text settings
        if (textSettings) textSettings.style.display = 'none';
    }

    // Update the field group description
    updateFieldGroupDescription(fieldGroupId);
    updateFieldConditionAvailability();
}

/**
 * Toggle pricing fields visibility for text fields
 */
function toggleTextPricingFields(fieldGroupId) {
    const checkbox = document.querySelector(`input[name="field_groups[${fieldGroupId}][adjust_pricing]"]`);
    const pricingFields = document.getElementById(`text-pricing-fields-${fieldGroupId}`);

    console.log('toggleTextPricingFields called for fieldGroupId:', fieldGroupId);
    console.log('Found checkbox:', !!checkbox, 'checked:', checkbox?.checked);
    console.log('Found pricingFields:', !!pricingFields);

    if (!checkbox || !pricingFields) return;

    if (checkbox.checked) {
        pricingFields.style.display = 'block';
        console.log('Showing pricing fields');
    } else {
        pricingFields.style.display = 'none';
        console.log('Hiding pricing fields');
    }
}

/**
 * Update pricing symbol for text fields based on price type
 */
function updateTextPricingSymbol(selectElement, fieldGroupId) {
    const symbolElement = document.getElementById(`text-price-symbol-${fieldGroupId}`);
    if (symbolElement) {
        switch (selectElement.value) {
            case 'p':
            case 'percent':
                symbolElement.textContent = '%';
                break;
            default:
                symbolElement.textContent = '$';
        }
    }
}

/**
 * Update pricing symbol based on price type
 */
function updatePricingSymbol(selectElement, optionId) {
    const priceType = selectElement.value;
    const symbolElement = document.getElementById(`price-symbol-${optionId}`);

    if (symbolElement) {
        switch (priceType) {
            case 'percentage':
                symbolElement.textContent = '%';
                break;
            case 'fixed':
            case 'qt':
            case 'none':
            case 'included':
            default:
                symbolElement.textContent = '$';
                break;
        }
    }
}

/**
 * Add a new option to a field group
 */
function addNewOption(fieldGroupId, optionData = null, optionSettings = {}) {
    if (!optionCounters[fieldGroupId]) {
        optionCounters[fieldGroupId] = 0;
    }
    optionCounters[fieldGroupId]++;
    const optionsContainer = document.getElementById(`options-container-${fieldGroupId}`);
    if (!optionsContainer) return;

    const { skipAutoUpdate = false } = optionSettings;
    const optionId = `${fieldGroupId}_${optionCounters[fieldGroupId]}`;
    const priceType = optionData?.price_type || 'none';
    const rawPrice = optionData?.price;
    const priceValue = typeof rawPrice === 'number' || (typeof rawPrice === 'string' && rawPrice.trim() !== '')
        ? String(rawPrice)
        : '';
    const optionName = escapeHtml(optionData?.name || optionData?.option_name || optionData?.label || '');
    const backendOptionId = optionData?.id;
    const hiddenIdInput = backendOptionId !== undefined && backendOptionId !== null
        ? `<input type="hidden" name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][id]" value="${escapeHtml(backendOptionId)}">`
        : '';
    const includedChecked = isTruthy(optionData?.included) ? 'checked' : '';
    const selectedChecked = isTruthy(optionData?.selected) ? 'checked' : '';

    // Check if this field group is a text type to determine if option names should be required
    const displayTypeSelect = document.querySelector(`select[name="field_groups[${fieldGroupId}][display_type]"]`);
    const isTextType = displayTypeSelect && displayTypeSelect.value === 'text';
    const requiredAttribute = isTextType ? 'data-was-required="true"' : 'required';

    const optionRow = document.createElement('div');
    optionRow.className = 'flex items-center gap-3 option-row';
    optionRow.id = `option-${optionId}`;

    optionRow.innerHTML = `
        <div class="flex-1">
            ${hiddenIdInput}
            <input type="text" name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][name]" placeholder="Option name"
                   value="${optionName}"
                   class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:border-sky-500 focus:ring-1 focus:ring-sky-500" ${requiredAttribute}>
        </div>

        <div class="flex-1">
            <select name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][price_type]" class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:border-sky-500 focus:ring-1 focus:ring-sky-500" onchange="updatePricingSymbol(this, '${optionId}')">
                <option value="none" ${priceType === 'none' ? 'selected' : ''}>No price change</option>
                <option value="fixed" ${priceType === 'fixed' ? 'selected' : ''}>Flat fee</option>
                <option value="qt" ${priceType === 'qt' ? 'selected' : ''}>Quantity based flat fee</option>
                <option value="percentage" ${priceType === 'percentage' ? 'selected' : ''}>Percentage based fee</option>
                <option value="included" ${priceType === 'included' ? 'selected' : ''}>Already included</option>
            </select>
        </div>

        <div class="flex-1">
            <div class="flex">
                <span id="price-symbol-${optionId}" class="inline-flex items-center px-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l">$</span>
                <input type="number" name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][price]" step="0.01" min="-999.99" max="999.99"
                       class="flex-1 text-sm border border-gray-300 rounded-r px-2 py-1.5 focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
                       placeholder="0.00" value="${escapeHtml(priceValue)}">
            </div>
        </div>

        <div class="flex items-center gap-4 ml-auto">
            <label class="w-16 flex justify-center">
                <input type="checkbox" name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][included]" value="1"
                       class="text-sky-600 border-gray-300 rounded focus:ring-sky-500" ${includedChecked}>
                <span class="sr-only">Included</span>
            </label>

            <label class="w-16 flex justify-center">
                <input type="checkbox" name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][selected]" value="1"
                       class="text-sky-600 border-gray-300 rounded focus:ring-sky-500" ${selectedChecked}>
                <span class="sr-only">Selected</span>
            </label>

            <button type="button" onclick="removeOption('${optionId}', ${fieldGroupId})"
                    class="w-8 text-gray-400 hover:text-red-600 p-1 rounded hover:bg-gray-100 transition-colors flex items-center justify-center"
                    title="Remove option">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 16 16">
                    <path fill="currentColor" d="M9.41,8l5.3-5.29a1,1,0,1,0-1.42-1.42L8,6.59,2.71,1.29A1,1,0,0,0,1.29,2.71L6.59,8l-5.3,5.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L8,9.41l5.29,5.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"/>
                </svg>
            </button>
        </div>
    `;

    optionsContainer.appendChild(optionRow);
    optionRow.dataset.optionId = backendOptionId !== undefined && backendOptionId !== null ? String(backendOptionId) : optionId;

    const optionNameInput = optionRow.querySelector(`input[name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][name]"]`);
    if (optionNameInput) {
        optionNameInput.addEventListener('input', () => refreshFieldConditionValueSources(fieldGroupId));
    }
    const priceTypeSelect = optionRow.querySelector(`select[name="field_groups[${fieldGroupId}][modifiers][${optionCounters[fieldGroupId]}][price_type]"]`);
    if (priceTypeSelect) {
        updatePricingSymbol(priceTypeSelect, optionId);
    }

    // If this field group is a text type, disable the newly added option inputs
    // const displayTypeSelect = document.querySelector(`select[name="field_groups[${fieldGroupId}][display_type]"]`);
    if (displayTypeSelect && displayTypeSelect.value === 'text') {
        const newOptionInputs = optionRow.querySelectorAll('input, select');
        newOptionInputs.forEach(input => {
            input.disabled = true;
            input.setAttribute('data-was-disabled-for-text', 'true');
            if (input.hasAttribute('required')) {
                input.removeAttribute('required');
                input.setAttribute('data-was-required', 'true');
            }
        });
    }

    if (!skipAutoUpdate) {
        updateFieldGroupTypeBasedOnOptions(fieldGroupId);
        updateFieldGroupDescription(fieldGroupId);
        updateOverallGroupType();
    }

    refreshFieldConditionValueSources(fieldGroupId);
    updateFieldConditionAvailability();
}

// Options management functions for old system
function renderOptions() {
    // Render options in the table format
    const tableContainer = document.getElementById('options-container-table');
    if (tableContainer) {
        tableContainer.innerHTML = options.map(option => `
            <div class="mb-2 grid grid-cols-12 items-center gap-3">
                <input type="text" value="${option.label}" class="col-span-5 rounded-md border border-gray-300 px-3 py-2 text-sm"
                       onchange="updateOptionLabel(${option.id}, this.value)" />
                <select class="col-span-4 rounded-md border border-gray-300 px-3 py-2 text-sm"
                        onchange="updateOptionPriceType(${option.id}, this.value)">
                    <option value="none" ${option.price_type === 'none' ? 'selected' : ''}>No price change</option>
                    <option value="fixed" ${option.price_type === 'fixed' ? 'selected' : ''}>Flat fee</option>
                    <option value="qt" ${option.price_type === 'qt' ? 'selected' : ''}>Quantity based flat fee</option>
                    <option value="percentage" ${option.price_type === 'percentage' ? 'selected' : ''}>Percentage based fee</option>
                    <option value="included" ${option.price_type === 'included' ? 'selected' : ''}>Already included</option>
                </select>
                <div class="col-span-2 flex">
                    <span id="price-symbol-table-${option.id}" class="inline-flex items-center px-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l">${option.price_type === 'percentage' ? '%' : '$'}</span>
                    <input type="number" step="0.01" value="${option.price}" class="flex-1 rounded-r border border-gray-300 px-3 py-2 text-sm"
                           onchange="updateOptionPrice(${option.id}, this.value)" />
                </div>
                <input type="checkbox" ${option.included ? 'checked' : ''} class="col-span-1 mx-auto h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                       onchange="updateOptionIncluded(${option.id})" />
               <input type="checkbox" ${option.selected ? 'checked' : ''} class="col-span-1 mx-auto h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                       onchange="updateOptionIncluded(${option.id})" />
            </div>
        `).join('');
    }

    // Also render in the separate Modifiers section if it exists
    const cardContainer = document.getElementById('options-container');
    if (cardContainer) {
        cardContainer.innerHTML = options.map(option => `
            <div class="bg-white border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                <div class="grid grid-cols-12 gap-4 items-center">
                    <!-- Drag Handle -->
                    <div class="col-span-1 flex justify-center">
                        <span class="text-gray-400 cursor-move">⋮⋮</span>
                    </div>

                    <!-- Option Label -->
                    <div class="col-span-4">
                        <input type="text" value="${option.label}"
                               class="w-full border-none bg-transparent text-sm font-medium focus:outline-none focus:ring-0 px-0"
                               onchange="updateOptionLabel(${option.id}, this.value)"
                               placeholder="Option name">
                    </div>

                    <!-- Adjust Pricing -->
                    <div class="col-span-2">
                        <select class="w-full border-gray-300 rounded text-xs px-2 py-1"
                                onchange="updateOptionPriceType(${option.id}, this.value)">
                            <option value="none" ${option.price_type === 'none' ? 'selected' : ''}>No price change</option>
                            <option value="fixed" ${option.price_type === 'fixed' ? 'selected' : ''}>Flat fee</option>
                            <option value="qt" ${option.price_type === 'qt' ? 'selected' : ''}>Quantity based flat fee</option>
                            <option value="percentage" ${option.price_type === 'percentage' ? 'selected' : ''}>Percentage based fee</option>
                            <option value="included" ${option.price_type === 'included' ? 'selected' : ''}>Already included</option>
                        </select>
                    </div>

                    <!-- Pricing Amount -->
                    <div class="col-span-2">
                        ${option.price_type === 'fixed' || option.price_type === 'qt' || option.price_type === 'percentage' ? `
                            <div class="flex">
                                <span id="price-symbol-card-${option.id}" class="inline-flex items-center px-1 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-xs rounded-l">${option.price_type === 'percentage' ? '%' : '$'}</span>
                                <input type="number" value="${option.price}" step="0.01" min="0"
                                       class="flex-1 border border-gray-300 rounded-r px-2 py-1 text-xs"
                                       onchange="updateOptionPrice(${option.id}, this.value)"
                                       placeholder="0.00">
                            </div>
                        ` : `
                            <span class="text-xs text-gray-400"></span>
                        `}
                    </div>

                    <!-- Included Checkbox -->
                    <div class="col-span-1 flex justify-center">
                        <input type="checkbox" ${option.included ? 'checked' : ''}
                               onchange="updateOptionIncluded(${option.id})"
                               class="h-4 w-4 text-blue-600 border-gray-300 rounded">
                    </div>

                    <!-- Selected Checkbox -->
                    <div class="col-span-1 flex justify-center">
                        <input type="checkbox" ${option.selected ? 'checked' : ''}
                               onchange="updateOptionSelected(${option.id})"
                               class="h-4 w-4 text-blue-600 border-gray-300 rounded">
                    </div>

                    <!-- Actions -->
                    <div class="col-span-1 flex justify-center">
                        <button type="button" onclick="removeOption(${option.id})"
                                class="text-gray-400 hover:text-red-600 text-sm">
                            <i class="uil uil-trash-alt"></i>
                        </button>
                    </div>
                </div>
            </div>
        `).join('');
    }
}

function addNewOptionForOldSystem() {
    console.log("Add new option clicked");
    const newId = Math.max(...options.map(o => o.id)) + 1;
    options.push({
        id: newId,
        label: 'New Option',
        price_type: 'none',
        price: 0,
        included: false,
        selected: false
    });
    renderOptions();
}

function updateOptionLabel(id, value) {
    const option = options.find(o => o.id === id);
    if (option) option.label = value;
}

function updateOptionPriceType(id, value) {
    const option = options.find(o => o.id === id);
    if (option) {
        option.price_type = value;

        // Update symbols for table and card views
        const tableSymbol = document.getElementById(`price-symbol-table-${id}`);
        const cardSymbol = document.getElementById(`price-symbol-card-${id}`);
        const symbol = (value === 'percentage') ? '%' : '$';

        if (tableSymbol) tableSymbol.textContent = symbol;
        if (cardSymbol) cardSymbol.textContent = symbol;

        renderOptions();
    }
}

function updateOptionPrice(id, value) {
    const option = options.find(o => o.id === id);
    if (option) option.price = parseFloat(value);
}

function updateOptionIncluded(id) {
    const option = options.find(o => o.id === id);
    if (option) option.included = !option.included;
}

function updateOptionSelected(id) {
    const option = options.find(o => o.id === id);
    if (option) option.selected = !option.selected;
}

function removeOptionFromOldSystem(id) {
    options = options.filter(o => o.id !== id);
    renderOptions();
}

// Conditions section functions
function showAddRuleForm() {
    document.getElementById('conditions-start-state').style.display = 'none';
    document.getElementById('conditions-add-state').style.display = 'block';

    // Add the first condition group
    addOrGroup();
}

function addOrGroup() {
    conditionGroupCounter++;
    const container = document.getElementById('conditions-rules-container');

    // Add "Or" separator if this is not the first group
    if (container.children.length > 0) {
        const orSeparator = document.createElement('div');
        orSeparator.className = 'text-center py-2';
        orSeparator.innerHTML = '<span class="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">Or</span>';
        container.appendChild(orSeparator);
    }

    // Create new condition group
    const groupDiv = document.createElement('div');
    groupDiv.className = 'condition-group';
    groupDiv.dataset.groupId = conditionGroupCounter;
    groupDiv.innerHTML = `
        <div class="space-y-2 condition-rules-list">
            <!-- Rules will be added here -->
        </div>
    `;

    container.appendChild(groupDiv);

    // Add the first rule to this group
    addConditionRule(conditionGroupCounter);
}

function addConditionRule(groupId) {
    conditionRuleCounter++;
    const rulesContainer = document.getElementById('conditions-rules-container');
    const group = rulesContainer
        ? rulesContainer.querySelector(`[data-group-id="${groupId}"] .condition-rules-list`)
        : document.querySelector(`[data-group-id="${groupId}"] .condition-rules-list`);

    // Hide "And" button on all existing rules in this group (only show on the last/newest one)
    const existingRules = group.querySelectorAll('.condition-rule');
    existingRules.forEach(rule => {
        const andButton = rule.querySelector('.and-button');
        if (andButton) {
            andButton.style.display = 'none';
        }
    });

    const ruleDiv = document.createElement('div');
    ruleDiv.className = 'flex items-center space-x-3 condition-rule';
    ruleDiv.dataset.ruleId = conditionRuleCounter;

    ruleDiv.innerHTML = `
        <select class="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500" onchange="updateConditionField(${conditionRuleCounter}, this.value)">
            <option value="product_category">Product category</option>
            <option value="product">Product</option>
            <option value="authentication">User Role</option>
            <option value="authentication">Authentication</option>
        </select>
        <select class="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 condition-operator" onchange="updateConditionOperator(${conditionRuleCounter}, this.value)">
            <option value="equals">Is equal to</option>
            <option value="not_equals">Is not equal to</option>
            <option value="contains">Contains</option>
            <option value="not_contains">Does not contain</option>
        </select>
        <div class="flex-1 relative condition-value-container"></div>
        <div class="flex items-center space-x-2">
            <button type="button" class="and-button inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200" onclick="addConditionRule(${groupId})">
                And
            </button>
            <button type="button" class="text-gray-400 hover:text-red-600" onclick="removeConditionRule(this, ${groupId})">
                <i class="uil uil-times text-lg"></i>
            </button>
        </div>
    `;

    group.appendChild(ruleDiv);

    // Render initial value control based on default operator
    const operator = ruleDiv.querySelector('.condition-operator').value;
    renderConditionValueControl(ruleDiv, operator, '');
}

function removeConditionRule(buttonElement, groupId) {
    const ruleElement = buttonElement.closest('.condition-rule');
    const group = buttonElement.closest('.condition-group');
    const rulesList = group.querySelector('.condition-rules-list');

    ruleElement.remove();

    // If this was the last rule in the group, remove the entire group
    if (rulesList.children.length === 0) {
        // Find and remove the "Or" separator before this group if it exists
        const prevElement = group.previousElementSibling;
        if (prevElement && prevElement.querySelector('span')?.textContent === 'Or') {
            prevElement.remove();
        }

        group.remove();

        // Check if no groups left, show start state
        const container = document.getElementById('conditions-rules-container');
        if (container.children.length === 0) {
            document.getElementById('conditions-add-state').style.display = 'none';
            document.getElementById('conditions-start-state').style.display = 'block';
        }
    } else {
        // If there are still rules in the group, show "And" button on the last rule
        const remainingRules = rulesList.querySelectorAll('.condition-rule');
        if (remainingRules.length > 0) {
            const lastRule = remainingRules[remainingRules.length - 1];
            const andButton = lastRule.querySelector('.and-button');
            if (andButton) {
                andButton.style.display = 'inline-flex';
            }
        }
    }
}

// Helper to render condition value control inside a rule row
function renderConditionValueControl(ruleRow, operator, currentValue) {
    const form = document.getElementById('modifier-group-form');
    let categories = [];
    let products = [];

    try {
        const categoriesAttr = form?.getAttribute('data-categories');
        if (categoriesAttr) {
            categories = JSON.parse(categoriesAttr);
        }
    } catch (e) {
        console.error('Error parsing categories:', e);
    }

    try {
        const productsAttr = form?.getAttribute('data-products');
        if (productsAttr) {
            products = JSON.parse(productsAttr);
        }
    } catch (e) {
        console.error('Error parsing products:', e);
    }

    const container = ruleRow.querySelector('.condition-value-container');
    if (!container) return;
    container.innerHTML = '';

    // Get the field type from the field select
    const fieldSelect = ruleRow.querySelector('select:first-child');
    const fieldType = fieldSelect ? fieldSelect.value : 'product_category';

    // Define field-specific placeholders
    const placeholders = {
        'product_category': 'Enter category names',
        'product': 'Enter product names',
        'product_type': 'Enter product types',
        'product_tag': 'Enter tags',
        'authentication': 'Enter roles'
    };

    if (operator === 'equals' || operator === 'not_equals') {
        // Single select dropdown for product_category and product
        if (fieldType === 'product_category') {
            const sel = document.createElement('select');
            sel.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
            sel.name = 'condition_value';
            const ph = document.createElement('option');
            ph.value = '';
            ph.textContent = 'Select category';
            sel.appendChild(ph);
            categories.forEach(c => {
                const opt = document.createElement('option');
                opt.value = String(c.id);
                opt.textContent = c.name;
                if (currentValue && String(currentValue) === String(c.id)) opt.selected = true;
                sel.appendChild(opt);
            });
            sel.addEventListener('change', function() { updateConditionValue(ruleRow.dataset.ruleId, this.value); });
            container.appendChild(sel);
        } else if (fieldType === 'product') {
            const sel = document.createElement('select');
            sel.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
            sel.name = 'condition_value';
            const ph = document.createElement('option');
            ph.value = '';
            ph.textContent = 'Select product';
            sel.appendChild(ph);
            products.forEach(p => {
                const opt = document.createElement('option');
                opt.value = String(p.id);
                opt.textContent = p.name;
                if (currentValue && String(currentValue) === String(p.id)) opt.selected = true;
                sel.appendChild(opt);
            });
            sel.addEventListener('change', function() { updateConditionValue(ruleRow.dataset.ruleId, this.value); });
            container.appendChild(sel);
        } else {
            // For other field types with equals/not_equals, use a simple input
            const input = document.createElement('input');
            input.type = 'text';
            input.name = 'condition_value';
            input.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
            if (currentValue) input.value = currentValue;
            input.placeholder = placeholders[fieldType] || 'Enter value';
            container.appendChild(input);
            input.addEventListener('change', function() { updateConditionValue(ruleRow.dataset.ruleId, this.value); });
        }
    } else {
        // Select2 tags input for contains/not_contains
        const input = document.createElement('input');
        input.type = 'text';
        input.name = 'condition_value';
        input.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
        if (currentValue) input.value = currentValue;
        input.placeholder = placeholders[fieldType] || 'Enter values (comma-separated)';
        container.appendChild(input);

        // Initialize Select2 after a brief delay to ensure DOM is ready
        setTimeout(() => {
            if (window.jQuery && jQuery.fn && jQuery.fn.select2) {
                jQuery(input).select2({
                    tags: true,
                    tokenSeparators: [','],
                    placeholder: placeholders[fieldType] || 'Enter values (comma-separated)'
                });
            }
        }, 100);

        input.addEventListener('change', function() { updateConditionValue(ruleRow.dataset.ruleId, this.value); });
        input.addEventListener('keydown', function(e) { handleConditionInput(ruleRow.dataset.ruleId, e); });
    }
}

// Hook operator change to re-render the value control
function updateConditionOperator(ruleId, operator) {
    const row = document.querySelector(`.condition-rule[data-rule-id="${ruleId}"]`);
    if (!row) return;
    const currentValEl = row.querySelector('[name="condition_value"]');
    const currentVal = currentValEl ? currentValEl.value : '';
    renderConditionValueControl(row, operator, currentVal);
}

function updateConditionField(ruleId, value) {
    if (!conditionData[ruleId]) conditionData[ruleId] = {};
    conditionData[ruleId].field = value;

    // Get current operator and re-render value control
    const ruleElement = document.querySelector(`[data-rule-id="${ruleId}"]`);
    const operatorSelect = ruleElement.querySelector('.condition-operator');
    const operator = operatorSelect ? operatorSelect.value : 'equals';

    // Re-render the value control based on field type and operator
    renderConditionValueControl(ruleElement, operator, '');
}

function updateConditionValue(ruleId, value) {
    if (!conditionData[ruleId]) conditionData[ruleId] = {};
    if (!conditionData[ruleId].values) conditionData[ruleId].values = [];

    // Don't add empty values
    if (!value.trim()) return;

    // Add value if not already present
    if (!conditionData[ruleId].values.includes(value)) {
        conditionData[ruleId].values.push(value);
        renderConditionValues(ruleId);
    }

    // Clear the input
    const ruleElement = document.querySelector(`[data-rule-id="${ruleId}"]`);
    const input = ruleElement.querySelector('.condition-value-input');
    input.value = '';
}

function handleConditionInput(ruleId, event) {
    if (event.key === 'Enter') {
        event.preventDefault();
        const value = event.target.value.trim();
        if (value) {
            updateConditionValue(ruleId, value);
        }
    }
}

function renderConditionValues(ruleId) {
    const ruleElement = document.querySelector(`[data-rule-id="${ruleId}"]`);
    const valuesContainer = ruleElement.querySelector('.selected-values');

    if (!conditionData[ruleId] || !conditionData[ruleId].values || conditionData[ruleId].values.length === 0) {
        valuesContainer.style.display = 'none';
        return;
    }

    valuesContainer.style.display = 'flex';
    valuesContainer.innerHTML = conditionData[ruleId].values.map(value => `
        <span class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md">
            <i class="uil uil-times mr-1 cursor-pointer" onclick="removeConditionValue(${ruleId}, '${value}')"></i>
            ${value}
        </span>
    `).join('');
}

function removeConditionValue(ruleId, value) {
    if (conditionData[ruleId] && conditionData[ruleId].values) {
        conditionData[ruleId].values = conditionData[ruleId].values.filter(v => v !== value);
        renderConditionValues(ruleId);
    }
}

/**
 * Remove an option from a field group
 */
function removeOption(optionId, fieldGroupId) {
    const optionRow = document.getElementById(`option-${optionId}`);
    if (optionRow) {
        optionRow.remove();
        updateFieldGroupTypeBasedOnOptions(fieldGroupId);
        updateFieldGroupDescription(fieldGroupId);
        updateOverallGroupType();
        refreshFieldConditionValueSources(fieldGroupId);
        updateFieldConditionAvailability();
    }
}

/**
 * Update field group type based on number of options
 */
function updateFieldGroupTypeBasedOnOptions(fieldGroupId) {
    const optionRows = document.querySelectorAll(`#options-container-${fieldGroupId} .option-row`);
    const optionCount = optionRows.length;

    const displayTypeSelect = document.querySelector(`select[name="field_groups[${fieldGroupId}][display_type]"]`);
    const maxSelectionsInput = document.querySelector(`input[name="field_groups[${fieldGroupId}][max_selections]"]`);

    // Auto-detect display type based on number of options
    if (optionCount <= 3) {
        // 1-3 options: single selection (radio)
        if (displayTypeSelect) displayTypeSelect.value = 'radio';
        if (maxSelectionsInput) maxSelectionsInput.value = 0;
    } else {
        // 4+ options: multiple selection (checkboxes)
        if (displayTypeSelect) displayTypeSelect.value = 'checkboxes';
        if (maxSelectionsInput) maxSelectionsInput.value = Math.ceil(optionCount / 2);
    }
}

// ============================
// FIELD-LEVEL CONDITIONS HELPERS
// ============================

function ensureFieldConditionsEntry(fieldGroupId) {
    const key = String(fieldGroupId);
    if (!fieldConditions[key]) {
        fieldConditions[key] = [];
    }
    return fieldConditions[key];
}

function getFieldKeyForGroup(fieldGroupId) {
    const groupElement = document.getElementById(`field-group-${fieldGroupId}`);
    if (!groupElement) {
        return null;
    }
    if (groupElement.dataset.fieldKey) {
        return groupElement.dataset.fieldKey;
    }
    const hiddenFieldId = groupElement.querySelector(`input[name="field_groups[${fieldGroupId}][field_id]"]`);
    if (hiddenFieldId) {
        groupElement.dataset.fieldKey = hiddenFieldId.value;
        return hiddenFieldId.value;
    }
    return null;
}

function getFieldGroupElementByFieldKey(fieldKey) {
    return Array.from(document.querySelectorAll('[id^="field-group-"]')).find(group => group.dataset.fieldKey === fieldKey) || null;
}

function getFieldOptionValuesByNumericId(fieldGroupId) {
    const optionRows = document.querySelectorAll(`#options-container-${fieldGroupId} .option-row`);
    return Array.from(optionRows).map((row, index) => {
        const nameInput = row.querySelector('input[name*="[name]"]');
        const label = nameInput?.value?.trim() || `Option ${index + 1}`;
        const optionId = row.dataset.optionId || row.id || `${fieldGroupId}_${index + 1}`;
        return {
            option_id: optionId,
            option_label: label
        };
    });
}

function getFieldOptionValuesByFieldKey(fieldKey) {
    const groupElement = getFieldGroupElementByFieldKey(fieldKey);
    if (!groupElement) {
        return [];
    }
    const numericId = groupElement.id.replace('field-group-', '');
    const displayType = groupElement.querySelector(`select[name="field_groups[${numericId}][display_type]"]`)?.value || 'radio';
    if (displayType === 'text') {
        return [];
    }
    return getFieldOptionValuesByNumericId(numericId);
}

function collectFieldMeta() {
    return Array.from(document.querySelectorAll('[id^="field-group-"]')).map(group => {
        const numericId = group.id.replace('field-group-', '');
        const fieldKey = getFieldKeyForGroup(numericId);
        const labelInput = group.querySelector(`input[name="field_groups[${numericId}][label]"]`);
        const label = labelInput?.value?.trim() || `Field ${numericId}`;
        const displayType = group.querySelector(`select[name="field_groups[${numericId}][display_type]"]`)?.value || 'radio';
        return {
            numericId,
            fieldKey,
            label,
            displayType,
            options: displayType === 'text' ? [] : getFieldOptionValuesByNumericId(numericId)
        };
    }).filter(meta => meta.fieldKey);
}

function getFieldMetaByKey(fieldKey) {
    if (!fieldKey) {
        return null;
    }
    return collectFieldMeta().find(meta => meta.fieldKey === fieldKey) || null;
}

function getConditionOperatorsForField(meta) {
    if (!meta || meta.displayType === 'text') {
        return [
            { value: 'empty', label: 'Value is empty' },
            { value: '!empty', label: 'Value is any value' },
            { value: '==', label: 'Value is equal to' },
            { value: '!=', label: 'Value is not equal to' },
            { value: '==contains', label: 'Value contains' }
        ];
    }

    return [
        { value: 'contains', label: 'Selection Contains' },
        { value: 'not_contains', label: 'Selection Does Not Contain' }
    ];
}

function normalizeConditionOperator(rawOperator, meta) {
    if (!rawOperator) {
        return null;
    }

    if (!meta || meta.displayType !== 'text') {
        if (rawOperator === 'not_contains') {
            return 'not_contains';
        }
        if (rawOperator === 'contains') {
            return 'contains';
        }
        return rawOperator;
    }

    switch (rawOperator) {
        case 'empty':
            return 'empty';
        case '!empty':
        case 'not_empty':
            return '!empty';
        case '==':
        case 'equals':
        case 'equal':
            return '==';
        case '!=':
        case 'not_equals':
            return '!=';
        case 'contains':
        case '==contains':
            return '==contains';
        default:
            return '==';
    }
}

function getAvailableConditionTargets(fieldGroupId) {
    const currentFieldKey = getFieldKeyForGroup(fieldGroupId);
    if (!currentFieldKey) {
        return [];
    }

    return collectFieldMeta().filter(meta => meta.fieldKey !== currentFieldKey);
}

function ensureFieldConditionGroupCounter(fieldGroupId) {
    const key = String(fieldGroupId);
    if (!fieldConditionGroupCounters[key]) {
        fieldConditionGroupCounters[key] = 0;
    }
    return key;
}

function createFieldConditionGroup(fieldGroupId, desiredGroupId = null) {
    const key = ensureFieldConditionGroupCounter(fieldGroupId);
    let groupId;
    if (desiredGroupId !== null && desiredGroupId !== undefined) {
        groupId = String(desiredGroupId);
        const numeric = parseInt(groupId, 10);
        if (!Number.isNaN(numeric)) {
            fieldConditionGroupCounters[key] = Math.max(fieldConditionGroupCounters[key], numeric);
        }
    } else {
        groupId = String(++fieldConditionGroupCounters[key]);
    }

    const groupWrapper = document.createElement('div');
    groupWrapper.className = 'condition-group field-condition-group';
    groupWrapper.dataset.groupId = groupId;

    const rulesList = document.createElement('div');
    rulesList.className = 'space-y-2 condition-rules-list';
    groupWrapper.appendChild(rulesList);

    return groupWrapper;
}

function appendFieldConditionGroup(container, groupElement) {
    if (container.querySelector('.condition-group')) {
        const orSeparator = document.createElement('div');
        orSeparator.className = 'field-condition-or text-center py-2';
        orSeparator.innerHTML = '<span class="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">Or</span>';
        container.appendChild(orSeparator);
    }
    container.appendChild(groupElement);
}

function getLastFieldConditionGroup(container) {
    const groups = container.querySelectorAll('.condition-group');
    return groups.length ? groups[groups.length - 1] : null;
}

function getFieldConditionData(fieldGroupId, conditionId) {
    const conditions = fieldConditions[String(fieldGroupId)];
    if (!conditions) {
        return null;
    }
    return conditions.find(condition => condition.id === conditionId) || null;
}

function addFieldCondition(fieldGroupId, conditionSeed = null, groupElement = null) {
    ensureFieldConditionsEntry(fieldGroupId);

    const container = document.getElementById(`field-conditions-container-${fieldGroupId}`);
    if (!container) {
        return;
    }

    const targets = getAvailableConditionTargets(fieldGroupId);
    if (targets.length === 0) {
        return;
    }

    const groupKey = ensureFieldConditionGroupCounter(fieldGroupId);

    let groupWrapper = groupElement;
    if (!groupWrapper && conditionSeed?.group_id) {
        groupWrapper = container.querySelector(`.condition-group[data-group-id="${conditionSeed.group_id}"]`);
        if (!groupWrapper) {
            groupWrapper = createFieldConditionGroup(fieldGroupId, conditionSeed.group_id);
            appendFieldConditionGroup(container, groupWrapper);
        }
    }

    if (!groupWrapper) {
        groupWrapper = getLastFieldConditionGroup(container);
    }

    if (!groupWrapper) {
        groupWrapper = createFieldConditionGroup(fieldGroupId);
        appendFieldConditionGroup(container, groupWrapper);
    }

    let groupId = groupWrapper.dataset.groupId;
    if (!groupId) {
        groupId = String(++fieldConditionGroupCounters[groupKey]);
        groupWrapper.dataset.groupId = groupId;
    }

    const rulesList = groupWrapper.querySelector('.condition-rules-list');
    if (!rulesList) {
        return;
    }

    const validSeedTarget = conditionSeed?.target_field_id && targets.some(target => target.fieldKey === conditionSeed.target_field_id);
    const selectedTarget = validSeedTarget ? conditionSeed.target_field_id : targets[0].fieldKey;
    const selectedTargetMeta = getFieldMetaByKey(selectedTarget);
    const operatorOptions = getConditionOperatorsForField(selectedTargetMeta);
    const normalizedOperator = normalizeConditionOperator(conditionSeed?.operator, selectedTargetMeta);

    const seedValues = Array.isArray(conditionSeed?.values)
        ? conditionSeed.values.map(value => ({
            option_id: value.option_id ?? value.id ?? value,
            option_label: value.option_label ?? value.label ?? String(value)
        }))
        : [];

    const initialValue = conditionSeed?.value ?? (seedValues[0]?.option_id ?? '');
    const initialLabel = conditionSeed?.value_label ?? (seedValues[0]?.option_label ?? initialValue);

    fieldConditionCounter++;
    const conditionValues = initialValue ? [{ option_id: initialValue, option_label: initialLabel || initialValue }] : [];

    const conditionData = {
        id: fieldConditionCounter,
        group_id: groupId,
        target_field_id: selectedTarget,
        operator: operatorOptions.some(opt => opt.value === normalizedOperator) ? normalizedOperator : operatorOptions[0].value,
        value: initialValue,
        value_label: initialLabel,
        values: conditionValues
    };

    fieldConditions[String(fieldGroupId)].push(conditionData);

    const ruleDiv = document.createElement('div');
    ruleDiv.className = 'flex items-center space-x-3 condition-rule';
    ruleDiv.dataset.fieldConditionId = String(conditionData.id);
    ruleDiv.dataset.groupId = groupId;
    ruleDiv.dataset.targetFieldId = conditionData.target_field_id;
    ruleDiv.dataset.targetFieldType = selectedTargetMeta?.displayType || 'radio';

    const fieldWrapper = document.createElement('div');
    fieldWrapper.className = 'md:w-52';
    const fieldSelect = document.createElement('select');
    fieldSelect.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 field-condition-field-select';
    targets.forEach(target => {
        const option = document.createElement('option');
        option.value = target.fieldKey;
        option.textContent = target.label;
        fieldSelect.appendChild(option);
    });
    fieldSelect.value = conditionData.target_field_id;
    fieldSelect.addEventListener('change', event => {
        handleFieldConditionFieldChange(fieldGroupId, conditionData.id, event.target.value);
    });
    fieldWrapper.appendChild(fieldSelect);

    const operatorWrapper = document.createElement('div');
    operatorWrapper.className = 'md:w-56';
    const operatorSelect = document.createElement('select');
    operatorSelect.className = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 field-condition-operator-select';
    operatorOptions.forEach(option => {
        const opt = document.createElement('option');
        opt.value = option.value;
        opt.textContent = option.label;
        operatorSelect.appendChild(opt);
    });
    operatorSelect.value = conditionData.operator;
    operatorSelect.addEventListener('change', event => {
        handleFieldConditionOperatorChange(fieldGroupId, conditionData.id, event.target.value);
    });
    operatorWrapper.appendChild(operatorSelect);

    const valuesWrapper = document.createElement('div');
    valuesWrapper.className = 'flex-1 relative';
    const valueSelect = document.createElement('select');
    valueSelect.className = 'field-condition-value-select w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
    valueSelect.addEventListener('change', event => {
        handleFieldConditionSelectChange(fieldGroupId, conditionData.id, event.target);
    });
    const valueInput = document.createElement('input');
    valueInput.type = 'text';
    valueInput.className = 'field-condition-value-input w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';
    valueInput.placeholder = 'Enter value';
    valueInput.addEventListener('input', event => {
        handleFieldConditionTextChange(fieldGroupId, conditionData.id, event.target.value);
    });

    valuesWrapper.appendChild(valueSelect);
    valuesWrapper.appendChild(valueInput);

    const actionsWrapper = document.createElement('div');
    actionsWrapper.className = 'flex items-center space-x-2';
    const andButton = document.createElement('button');
    andButton.type = 'button';
    andButton.className = 'and-button field-condition-and inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200';
    andButton.textContent = 'And';
    andButton.addEventListener('click', () => addFieldCondition(fieldGroupId, null, groupWrapper));
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'text-gray-400 hover:text-red-600';
    removeButton.innerHTML = '<i class="uil uil-times text-lg"></i>';
    removeButton.addEventListener('click', () => removeFieldCondition(fieldGroupId, conditionData.id));
    actionsWrapper.appendChild(andButton);
    actionsWrapper.appendChild(removeButton);

    ruleDiv.appendChild(fieldWrapper);
    ruleDiv.appendChild(operatorWrapper);
    ruleDiv.appendChild(valuesWrapper);
    ruleDiv.appendChild(actionsWrapper);

    rulesList.appendChild(ruleDiv);

    updateFieldConditionValueOptions(fieldGroupId, conditionData.id);
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionData.id);
    updateFieldConditionAvailability();
    updateFieldConditionAndButtons(fieldGroupId);
}

function handleFieldConditionFieldChange(fieldGroupId, conditionId, targetFieldKey) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }
    condition.target_field_id = targetFieldKey;
    condition.value = '';
    condition.value_label = '';
    condition.values = [];

    const conditionElement = document.querySelector(`.condition-rule[data-field-condition-id="${conditionId}"]`);
    if (conditionElement) {
        conditionElement.dataset.targetFieldId = targetFieldKey;
        const meta = getFieldMetaByKey(targetFieldKey);
        conditionElement.dataset.targetFieldType = meta?.displayType || 'radio';
        condition.operator = normalizeConditionOperator(condition.operator, meta) || getConditionOperatorsForField(meta)[0].value;
        updateFieldConditionOperatorOptions(fieldGroupId, conditionId);
    }

    updateFieldConditionValueOptions(fieldGroupId, conditionId);
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionId);
}

function handleFieldConditionOperatorChange(fieldGroupId, conditionId, operator) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }
    condition.operator = operator;
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionId);
}

function addFieldConditionGroup(fieldGroupId) {
    const container = document.getElementById(`field-conditions-container-${fieldGroupId}`);
    if (!container) {
        return;
    }

    const orButton = document.getElementById(`add-condition-group-btn-${fieldGroupId}`);
    if (orButton && orButton.disabled) {
        return;
    }

    const targets = getAvailableConditionTargets(fieldGroupId);
    if (targets.length === 0) {
        return;
    }

    const groupWrapper = createFieldConditionGroup(fieldGroupId);
    appendFieldConditionGroup(container, groupWrapper);
    addFieldCondition(fieldGroupId, null, groupWrapper);
}

function handleFieldConditionSelectChange(fieldGroupId, conditionId, selectElement) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }
    const option = selectElement.options[selectElement.selectedIndex];
    const value = option ? option.value : '';
    const label = option ? option.textContent : '';
    condition.value = value;
    condition.value_label = label;
    condition.values = value ? [{ option_id: value, option_label: label }] : [];
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionId);
}

function handleFieldConditionTextChange(fieldGroupId, conditionId, newValue) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }
    condition.value = newValue;
    condition.value_label = newValue;
    condition.values = newValue ? [{ option_id: newValue, option_label: newValue }] : [];
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionId);
}

function removeFieldCondition(fieldGroupId, conditionId) {
    const key = String(fieldGroupId);
    const conditions = fieldConditions[key];
    if (conditions) {
        fieldConditions[key] = conditions.filter(condition => condition.id !== conditionId);
        if (fieldConditions[key].length === 0) {
            delete fieldConditions[key];
        }
    }

    const container = document.getElementById(`field-conditions-container-${fieldGroupId}`);
    if (!container) {
        updateFieldConditionAvailability();
        updateFieldConditionAndButtons(fieldGroupId);
        return;
    }

    const conditionElement = container.querySelector(`.condition-rule[data-field-condition-id="${conditionId}"]`);
    const groupWrapper = conditionElement?.closest('.field-condition-group');
    const rulesList = groupWrapper?.querySelector('.condition-rules-list');

    if (conditionElement) {
        conditionElement.remove();
    }

    if (groupWrapper && rulesList && rulesList.querySelectorAll('.condition-rule').length === 0) {
        const previous = groupWrapper.previousElementSibling;
        const next = groupWrapper.nextElementSibling;
        groupWrapper.remove();
        if (previous && previous.classList && previous.classList.contains('field-condition-or')) {
            previous.remove();
        } else if (next && next.classList && next.classList.contains('field-condition-or')) {
            next.remove();
        }
    }

    updateFieldConditionAvailability();
    updateFieldConditionAndButtons(fieldGroupId);
}

function clearFieldConditionsForGroup(fieldGroupId) {
    if (fieldConditions[String(fieldGroupId)]) {
        delete fieldConditions[String(fieldGroupId)];
    }
    fieldConditionGroupCounters[String(fieldGroupId)] = 0;
    const container = document.getElementById(`field-conditions-container-${fieldGroupId}`);
    if (container) {
        container.innerHTML = '';
    }
    updateFieldConditionAndButtons(fieldGroupId);
    updateFieldConditionAvailability();
}

function removeFieldConditionsReferencingField(fieldKey) {
    Object.keys(fieldConditions).forEach(parentId => {
        fieldConditions[parentId] = fieldConditions[parentId].filter(condition => condition.target_field_id !== fieldKey);
        if (fieldConditions[parentId].length === 0) {
            delete fieldConditions[parentId];
        }
        const container = document.getElementById(`field-conditions-container-${parentId}`);
        if (container) {
            Array.from(container.querySelectorAll('.condition-rule')).forEach(conditionElement => {
                if (conditionElement.dataset.targetFieldId === fieldKey) {
                    const previous = conditionElement.previousElementSibling;
                    const next = conditionElement.nextElementSibling;
                    conditionElement.remove();
                    if (previous && previous.classList && previous.classList.contains('field-condition-or')) {
                        previous.remove();
                    } else if (next && next.classList && next.classList.contains('field-condition-or')) {
                        next.remove();
                    }
                }
            });

            Array.from(container.querySelectorAll('.field-condition-group')).forEach(groupWrapper => {
                const rulesList = groupWrapper.querySelector('.condition-rules-list');
                if (!rulesList || rulesList.querySelectorAll('.condition-rule').length === 0) {
                    const previous = groupWrapper.previousElementSibling;
                    const next = groupWrapper.nextElementSibling;
                    groupWrapper.remove();
                    if (previous && previous.classList && previous.classList.contains('field-condition-or')) {
                        previous.remove();
                    } else if (next && next.classList && next.classList.contains('field-condition-or')) {
                        next.remove();
                    }
                }
            });
        }
        updateFieldConditionAndButtons(Number(parentId));
    });

    updateFieldConditionAvailability();
}

function updateFieldConditionValueOptions(fieldGroupId, conditionId, cachedOptions) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }

    const ruleElement = document.querySelector(`.condition-rule[data-field-condition-id="${conditionId}"]`);
    if (!ruleElement) {
        return;
    }

    const valueSelect = ruleElement.querySelector('.field-condition-value-select');
    const meta = getFieldMetaByKey(condition.target_field_id);

    if (!valueSelect || (meta && meta.displayType === 'text')) {
        return;
    }

    const availableOptions = cachedOptions || getFieldOptionValuesByFieldKey(condition.target_field_id);

    valueSelect.innerHTML = '';

    if (!availableOptions.length) {
        valueSelect.disabled = true;
        condition.value = '';
        condition.value_label = '';
        condition.values = [];
        return;
    }

    valueSelect.disabled = false;
    let hasMatch = false;

    availableOptions.forEach(option => {
        const optionElement = document.createElement('option');
        optionElement.value = option.option_id;
        optionElement.textContent = option.option_label;
        if (condition.value && condition.value === option.option_id) {
            optionElement.selected = true;
            hasMatch = true;
            condition.value_label = option.option_label;
        }
        valueSelect.appendChild(optionElement);
    });

    if (!hasMatch) {
        const first = availableOptions[0];
        valueSelect.value = first.option_id;
        condition.value = first.option_id;
        condition.value_label = first.option_label;
        condition.values = [{ option_id: first.option_id, option_label: first.option_label }];
    } else {
        condition.values = condition.value
            ? [{ option_id: condition.value, option_label: condition.value_label || condition.value }]
            : [];
    }
}

function refreshFieldConditionTargetOptionsForGroup(fieldGroupId) {
    const conditions = fieldConditions[String(fieldGroupId)];
    if (!conditions || conditions.length === 0) {
        return;
    }
    const targets = getAvailableConditionTargets(fieldGroupId);
    conditions.forEach(condition => {
        const conditionElement = document.querySelector(`.condition-rule[data-field-condition-id="${condition.id}"]`);
        if (!conditionElement) {
            return;
        }
        const selectElement = conditionElement.querySelector('.field-condition-field-select');
        if (!selectElement) {
            return;
        }
        const previousValue = condition.target_field_id;
        selectElement.innerHTML = '';
        if (targets.length === 0) {
            selectElement.disabled = true;
            updateFieldConditionValueOptions(fieldGroupId, condition.id, []);
            updateFieldConditionValueControlDisplay(fieldGroupId, condition.id);
            return;
        }
        selectElement.disabled = false;
        targets.forEach(target => {
            const option = document.createElement('option');
            option.value = target.fieldKey;
            option.textContent = target.label;
            selectElement.appendChild(option);
        });

        if (targets.some(target => target.fieldKey === previousValue)) {
            selectElement.value = previousValue;
        } else if (targets.length > 0) {
            const newTarget = targets[0].fieldKey;
            selectElement.value = newTarget;
            condition.target_field_id = newTarget;
            condition.values = [];
        }

        const meta = getFieldMetaByKey(condition.target_field_id);
        conditionElement.dataset.targetFieldId = condition.target_field_id;
        conditionElement.dataset.targetFieldType = meta?.displayType || 'radio';
        condition.operator = normalizeConditionOperator(condition.operator, meta) || getConditionOperatorsForField(meta)[0].value;
        updateFieldConditionOperatorOptions(fieldGroupId, condition.id);
        updateFieldConditionValueOptions(fieldGroupId, condition.id);
    });

    updateFieldConditionAndButtons(fieldGroupId);
}

function refreshFieldConditionValueSources(fieldGroupId) {
    const fieldKey = getFieldKeyForGroup(fieldGroupId);
    if (!fieldKey) {
        return;
    }
    const fieldMeta = getFieldMetaByKey(fieldKey);
    if (fieldMeta && fieldMeta.displayType === 'text') {
        return;
    }
    const availableOptions = getFieldOptionValuesByFieldKey(fieldKey);
    Object.keys(fieldConditions).forEach(parentId => {
        const conditions = fieldConditions[parentId];
        if (!conditions) {
            return;
        }
        conditions.forEach(condition => {
            if (condition.target_field_id === fieldKey) {
                condition.values = condition.values.filter(value => availableOptions.some(option => option.option_id === value.option_id));
                if (condition.values.length > 0) {
                    condition.value = condition.values[0].option_id;
                    condition.value_label = condition.values[0].option_label;
                } else if (availableOptions.length > 0) {
                    const first = availableOptions[0];
                    condition.value = first.option_id;
                    condition.value_label = first.option_label;
                    condition.values = [{ option_id: first.option_id, option_label: first.option_label }];
                } else {
                    condition.value = '';
                    condition.value_label = '';
                    condition.values = [];
                }
                updateFieldConditionValueOptions(Number(parentId), condition.id, availableOptions);
            }
        });
    });
}

function updateFieldConditionOperatorOptions(fieldGroupId, conditionId) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }
    const conditionElement = document.querySelector(`.condition-rule[data-field-condition-id="${conditionId}"]`);
    if (!conditionElement) {
        return;
    }
    const operatorSelect = conditionElement.querySelector('.field-condition-operator-select');
    if (!operatorSelect) {
        return;
    }
    const meta = getFieldMetaByKey(condition.target_field_id);
    const operatorOptions = getConditionOperatorsForField(meta);
    operatorSelect.innerHTML = '';
    operatorOptions.forEach(option => {
        const opt = document.createElement('option');
        opt.value = option.value;
        opt.textContent = option.label;
        operatorSelect.appendChild(opt);
    });
    if (!operatorOptions.some(option => option.value === condition.operator)) {
        condition.operator = operatorOptions[0].value;
    }
    operatorSelect.value = condition.operator;
    updateFieldConditionValueControlDisplay(fieldGroupId, conditionId);
}

function updateFieldConditionValueControlDisplay(fieldGroupId, conditionId) {
    const condition = getFieldConditionData(fieldGroupId, conditionId);
    if (!condition) {
        return;
    }

    const ruleElement = document.querySelector(`.condition-rule[data-field-condition-id="${conditionId}"]`);
    if (!ruleElement) {
        return;
    }

    const valueSelect = ruleElement.querySelector('.field-condition-value-select');
    const valueInput = ruleElement.querySelector('.field-condition-value-input');
    const meta = getFieldMetaByKey(condition.target_field_id);

    if (meta && meta.displayType === 'text') {
        if (valueSelect) {
            valueSelect.style.display = 'none';
            valueSelect.disabled = true;
        }
        if (valueInput) {
            valueInput.style.display = '';
            const disableTextInput = condition.operator === 'empty' || condition.operator === '!empty';
            valueInput.disabled = disableTextInput;
            if (disableTextInput) {
                condition.value = '';
                condition.value_label = '';
                condition.values = [];
                valueInput.value = '';
            } else {
                valueInput.value = condition.value || '';
            }
        }
    } else {
        if (valueSelect) {
            valueSelect.style.display = '';
            valueSelect.disabled = false;
        }
        if (valueInput) {
            valueInput.style.display = 'none';
        }
    }
}

function updateFieldConditionAndButtons(fieldGroupId) {
    const container = document.getElementById(`field-conditions-container-${fieldGroupId}`);
    if (!container) {
        return;
    }

    Array.from(container.querySelectorAll('.field-condition-group')).forEach(groupWrapper => {
        const rulesList = groupWrapper.querySelector('.condition-rules-list');
        if (!rulesList) {
            return;
        }
        const rules = Array.from(rulesList.querySelectorAll('.condition-rule'));
        const totalRules = rules.length;
        rules.forEach((rule, index) => {
            const andButton = rule.querySelector('.field-condition-and');
            if (!andButton) {
                return;
            }
            if (index === totalRules - 1) {
                andButton.style.display = '';
            } else {
                andButton.style.display = 'none';
            }
        });
    });
}

function updateFieldConditionAvailability() {
    const allMeta = collectFieldMeta();

    allMeta.forEach(meta => {
        const helper = document.getElementById(`field-condition-helper-${meta.numericId}`);
        const button = document.getElementById(`add-condition-btn-${meta.numericId}`);
        const orButton = document.getElementById(`add-condition-group-btn-${meta.numericId}`);
        if (!helper || !button || !orButton) {
            return;
        }

        const defaultMessage = helper.dataset.defaultMessage || helper.textContent;
        const availableTargets = allMeta.filter(other => other.fieldKey !== meta.fieldKey);
        const canUseConditions = availableTargets.length > 0;

        if (canUseConditions) {
            button.disabled = false;
            button.removeAttribute('disabled');
            orButton.disabled = false;
            orButton.removeAttribute('disabled');
            helper.style.display = 'none';
            helper.textContent = defaultMessage;
        } else {
            button.disabled = true;
            if (!button.hasAttribute('disabled')) {
                button.setAttribute('disabled', 'disabled');
            }
            orButton.disabled = true;
            if (!orButton.hasAttribute('disabled')) {
                orButton.setAttribute('disabled', 'disabled');
            }
            helper.style.display = '';
            helper.textContent = defaultMessage;
        }

        refreshFieldConditionTargetOptionsForGroup(meta.numericId);
    });
}

function processPendingFieldConditions() {
    if (!pendingFieldConditions.length) {
        return;
    }
    const pending = pendingFieldConditions.splice(0, pendingFieldConditions.length);
    pending.forEach(entry => {
        const { fieldGroupNumericId, conditions } = entry;
        if (!Array.isArray(conditions) || conditions.length === 0) {
            return;
        }
        ensureFieldConditionsEntry(fieldGroupNumericId);
        const sortedConditions = conditions.slice().sort((a, b) => {
            const aGroup = parseInt(a.group_id || 0, 10);
            const bGroup = parseInt(b.group_id || 0, 10);
            return aGroup - bGroup;
        });
        sortedConditions.forEach(condition => addFieldCondition(fieldGroupNumericId, condition));
    });
}

/**
 * Update overall group type based on all field groups
 */
function updateOverallGroupType() {
    const allOptionRows = document.querySelectorAll('.option-row');
    const totalOptions = allOptionRows.length;
    const fieldGroups = document.querySelectorAll('[id^="field-group-"]').length;

    const singleTypeRadio = document.getElementById('single_type');
    const multipleTypeRadio = document.getElementById('multiple_type');

    if (singleTypeRadio && multipleTypeRadio) {
        // Determine overall group type based on field groups and total options
        if (fieldGroups === 1 && totalOptions <= 3) {
            singleTypeRadio.checked = true;
            multipleTypeRadio.checked = false;
        } else {
            singleTypeRadio.checked = false;
            multipleTypeRadio.checked = true;
        }
    }
}

function isHiddenForValidation(element) {
    if (!element || element.disabled) {
        return true;
    }

    if (element.type === 'hidden') {
        return true;
    }

    if (element.closest('[hidden]')) {
        return true;
    }

    let current = element;
    while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return true;
        }
        current = current.parentElement;
    }

    return false;
}

function syncHiddenModifierValidation(form = document.getElementById('modifier-group-form')) {
    if (!form) {
        return;
    }

    const optionInputs = form.querySelectorAll('.option-row input, .option-row select, .option-row textarea');

    optionInputs.forEach(input => {
        const hidden = isHiddenForValidation(input);

        if (hidden) {
            if (input.hasAttribute('required')) {
                input.removeAttribute('required');
                input.setAttribute('data-was-required', 'true');
            }
            return;
        }

        if (!input.disabled && input.getAttribute('data-was-required') === 'true') {
            input.setAttribute('required', '');
            input.removeAttribute('data-was-required');
        }
    });
}

/**
 * Setup form validation
 */
function setupFormValidation() {
    const form = document.getElementById('modifier-group-form') || document.querySelector('form');
    if (form) {
        const submitButtons = form.id
            ? document.querySelectorAll(`button[type="submit"][form="${form.id}"], #${form.id} button[type="submit"]`)
            : form.querySelectorAll('button[type="submit"]');
        submitButtons.forEach(button => {
            button.addEventListener('click', function() {
                syncHiddenModifierValidation(form);
            });
        });

        form.addEventListener('submit', function(e) {
            syncHiddenModifierValidation(form);

            const groupName = document.querySelector('input[name="group_name"]');
            if (groupName && !groupName.value.trim()) {
                alert('Please enter a group name');
                e.preventDefault();
                return;
            }
        });
    }
}

/**
 * Setup group type radio button listeners
 */
function setupGroupTypeListeners() {
    const singleTypeRadio = document.getElementById('single_type');
    const multipleTypeRadio = document.getElementById('multiple_type');

    if (singleTypeRadio) {
        singleTypeRadio.addEventListener('change', function() {
            if (this.checked) {
                // Update all field groups to single selection
                document.querySelectorAll('[name*="[display_type]"]').forEach(select => {
                    select.value = 'radio';
                });
                document.querySelectorAll('[name*="[max_selections]"]').forEach(input => {
                    input.value = 1;
                });
            }
        });
    }

    if (multipleTypeRadio) {
        multipleTypeRadio.addEventListener('change', function() {
            if (this.checked) {
                // Update all field groups to multiple selection
                document.querySelectorAll('[name*="[display_type]"]').forEach(select => {
                    select.value = 'checkboxes';
                });
                document.querySelectorAll('[name*="[max_selections]"]').forEach(input => {
                    const fieldGroupId = input.name.match(/field_groups\[(\d+)\]/)?.[1];
                    if (fieldGroupId) {
                        const optionCount = document.querySelectorAll(`#options-container-${fieldGroupId} .option-row`).length;
                        input.value = Math.max(2, Math.ceil(optionCount / 2));
                    }
                });
            }
        });
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    initializeModifierGroups();
    syncHiddenModifierValidation();

    // Only initialize if we're on a page that has the options container
    if (document.getElementById('options-container-table')) {
        renderOptions();
    }

    // Initialize existing condition rules
    initializeExistingConditionRules();
});

// Initialize existing condition rules that were rendered server-side
function initializeExistingConditionRules() {
    const existingRules = document.querySelectorAll('.condition-rule[data-rule-id]');
    existingRules.forEach(ruleRow => {
        const operatorSelect = ruleRow.querySelector('.condition-operator');
        if (operatorSelect) {
            const operator = operatorSelect.value;
            const currentValueEl = ruleRow.querySelector('[name="condition_value"]');
            const currentValue = currentValueEl ? currentValueEl.value : '';
            renderConditionValueControl(ruleRow, operator, currentValue);
        }
    });
}

// Export functions for global access (if needed)
window.ModifierGroups = {
    toggleSection,
    toggleFieldGroupContent,
    addNewFieldGroup,
    removeFieldGroup,
    duplicateFieldGroup,
    updateFieldGroupName,
    updateFieldGroupDescription,
    handleFieldTypeChange,
    toggleTextPricingFields,
    updateTextPricingSymbol,
    updatePricingSymbol,
    addNewOption,
    removeOption,
    addFieldCondition,
    removeFieldCondition,
    addFieldConditionGroup,
    updateFieldConditionAvailability,
    updateFieldGroupTypeBasedOnOptions,
    updateOverallGroupType
};

// Also make them available globally for backward compatibility
if (typeof window !== 'undefined') {
    window.toggleSection = toggleSection;
    window.toggleFieldGroupContent = toggleFieldGroupContent;
    window.addNewFieldGroup = addNewFieldGroup;
    window.removeFieldGroup = removeFieldGroup;
    window.duplicateFieldGroup = duplicateFieldGroup;
    window.updateFieldGroupName = updateFieldGroupName;
    window.updateFieldGroupDescription = updateFieldGroupDescription;
    window.populateFieldGroups = populateFieldGroups;
    window.handleFieldTypeChange = handleFieldTypeChange;
    window.toggleTextPricingFields = toggleTextPricingFields;
    window.updateTextPricingSymbol = updateTextPricingSymbol;
    window.updatePricingSymbol = updatePricingSymbol;
    window.addFieldCondition = addFieldCondition;
    window.removeFieldCondition = removeFieldCondition;
    window.addFieldConditionGroup = addFieldConditionGroup;
    // Handle both addNewOption() for old system and addNewOption(fieldGroupId) for new system
    window.addNewOption = function(fieldGroupId) {
        if (fieldGroupId) {
            // New field group system
            return addNewOption(fieldGroupId);
        } else {
            // Old options system
            return addNewOptionForOldSystem();
        }
    };
    window.updateOptionLabel = updateOptionLabel;
    window.updateOptionPriceType = updateOptionPriceType;
    window.updateOptionPrice = updateOptionPrice;
    window.updateOptionIncluded = updateOptionIncluded;
    window.updateOptionSelected = updateOptionSelected;
    // Handle both removeOption(id) for old system and removeOption(optionId, fieldGroupId) for new system
    window.removeOption = function(idOrOptionId, fieldGroupId) {
        if (fieldGroupId !== undefined) {
            // New field group system - removeOption(optionId, fieldGroupId)
            return removeOption(idOrOptionId, fieldGroupId);
        } else {
            // Old options system - removeOption(id)
            return removeOptionFromOldSystem(idOrOptionId);
        }
    };
    window.showAddRuleForm = showAddRuleForm;
    window.addOrGroup = addOrGroup;
    window.addConditionRule = addConditionRule;
    window.removeConditionRule = removeConditionRule;
    window.updateConditionField = updateConditionField;
    window.updateConditionOperator = updateConditionOperator;
    window.updateConditionValue = updateConditionValue;
    window.handleConditionInput = handleConditionInput;
    window.removeConditionValue = removeConditionValue;
}

// =======================
// MODIFIER CONDITIONS MANAGEMENT
// =======================

/**
 * Add a modifier condition
 */
function addModifierCondition() {
    modifierConditionCounter++;
    const conditionsContainer = document.getElementById('modifier-conditions-container');

    if (!conditionsContainer) return;

    const conditionDiv = document.createElement('div');
    conditionDiv.className = 'border border-gray-200 rounded-lg p-4 mb-4';
    conditionDiv.id = `modifier-condition-${modifierConditionCounter}`;

    conditionDiv.innerHTML = `
        <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-medium text-gray-800">Condition ${modifierConditionCounter}</h4>
            <button type="button" onclick="removeModifierCondition(${modifierConditionCounter})"
                    class="text-gray-400 hover:text-red-600 text-sm">
                Remove
            </button>
        </div>

        <div class="grid grid-cols-12 gap-4">
            <!-- Condition Type -->
            <div class="col-span-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">Type</label>
                <select name="modifier_conditions[${modifierConditionCounter}][condition_type]"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                        onchange="updateConditionType(${modifierConditionCounter}, this.value)">
                    <option value="show_hide">Show/Hide</option>
                    <option value="price_adjust">Price Adjust</option>
                    <option value="require">Require</option>
                    <option value="disable">Disable</option>
                </select>
            </div>

            <!-- Trigger Modifier -->
            <div class="col-span-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">When</label>
                <select name="modifier_conditions[${modifierConditionCounter}][trigger_modifier_id]"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                    <option value="">Select trigger...</option>
                    ${getTriggerModifierOptions()}
                </select>
            </div>

            <!-- Trigger Value -->
            <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">Value</label>
                <input type="text" name="modifier_conditions[${modifierConditionCounter}][trigger_value]"
                       class="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                       placeholder="Optional">
            </div>

            <!-- Action Modifiers -->
            <div class="col-span-3">
                <label class="block text-xs font-medium text-gray-700 mb-1">Affect</label>
                <select multiple name="modifier_conditions[${modifierConditionCounter}][action_modifier_ids][]"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                    ${getActionModifierOptions()}
                </select>
            </div>

            <!-- Parameters -->
            <div class="col-span-1">
                <label class="block text-xs font-medium text-gray-700 mb-1">Params</label>
                <button type="button" onclick="showConditionParameters(${modifierConditionCounter})"
                        class="w-full text-xs bg-gray-100 border border-gray-300 rounded px-2 py-1.5 hover:bg-gray-200">
                    Edit
                </button>
            </div>
        </div>

        <!-- Parameters Panel (Hidden by default) -->
        <div id="condition-params-${modifierConditionCounter}" class="mt-3 p-3 bg-gray-50 rounded hidden">
            <label class="block text-xs font-medium text-gray-700 mb-1">Action Parameters (JSON)</label>
            <textarea name="modifier_conditions[${modifierConditionCounter}][action_parameters]"
                      rows="3" class="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                      placeholder='{"action": "hide", "adjustment": 0.5, "adjustment_type": "fixed"}'></textarea>
        </div>
    `;

    conditionsContainer.appendChild(conditionDiv);
    modifierConditions[modifierConditionCounter] = {
        condition_type: 'show_hide',
        trigger_modifier_id: '',
        trigger_value: '',
        action_modifier_ids: [],
        action_parameters: {}
    };
}

function removeModifierCondition(conditionId) {
    const conditionDiv = document.getElementById(`modifier-condition-${conditionId}`);
    if (conditionDiv) {
        conditionDiv.remove();
        delete modifierConditions[conditionId];
    }
}

function updateConditionType(conditionId, type) {
    if (modifierConditions[conditionId]) {
        modifierConditions[conditionId].condition_type = type;
    }
}

function showConditionParameters(conditionId) {
    const paramsPanel = document.getElementById(`condition-params-${conditionId}`);
    if (paramsPanel) {
        paramsPanel.classList.toggle('hidden');
    }
}

function getTriggerModifierOptions() {
    // This would normally be populated from actual modifier data
    let options = '<option value="">Select trigger...</option>';

    // Get all modifiers from field groups
    document.querySelectorAll('[id^="field-group-"]').forEach((fieldGroup, groupIndex) => {
        const groupName = fieldGroup.querySelector('.field-group-name')?.textContent || `Group ${groupIndex + 1}`;
        fieldGroup.querySelectorAll('.option-row').forEach((optionRow, optionIndex) => {
            const optionName = optionRow.querySelector('input[type="text"]')?.value || `Option ${optionIndex + 1}`;
            options += `<option value="${groupIndex}_${optionIndex}">${groupName}: ${optionName}</option>`;
        });
    });

    return options;
}

function getActionModifierOptions() {
    // Similar to getTriggerModifierOptions but for multiple selection
    let options = '';

    document.querySelectorAll('[id^="field-group-"]').forEach((fieldGroup, groupIndex) => {
        const groupName = fieldGroup.querySelector('.field-group-name')?.textContent || `Group ${groupIndex + 1}`;
        fieldGroup.querySelectorAll('.option-row').forEach((optionRow, optionIndex) => {
            const optionName = optionRow.querySelector('input[type="text"]')?.value || `Option ${optionIndex + 1}`;
            options += `<option value="${groupIndex}_${optionIndex}">${groupName}: ${optionName}</option>`;
        });
    });

    return options;
}

// =======================
// MODIFIER RULES MANAGEMENT
// =======================

/**
 * Add a modifier rule
 */
function addModifierRule() {
    modifierRuleCounter++;
    const rulesContainer = document.getElementById('modifier-rules-container');

    if (!rulesContainer) return;

    const ruleDiv = document.createElement('div');
    ruleDiv.className = 'border border-gray-200 rounded-lg p-4 mb-4';
    ruleDiv.id = `modifier-rule-${modifierRuleCounter}`;

    ruleDiv.innerHTML = `
        <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-medium text-gray-800">Rule ${modifierRuleCounter}</h4>
            <button type="button" onclick="removeModifierRule(${modifierRuleCounter})"
                    class="text-gray-400 hover:text-red-600 text-sm">
                Remove
            </button>
        </div>

        <div class="grid grid-cols-12 gap-4 mb-3">
            <!-- Rule Name -->
            <div class="col-span-6">
                <label class="block text-xs font-medium text-gray-700 mb-1">Rule Name</label>
                <input type="text" name="modifier_rules[${modifierRuleCounter}][rule_name]"
                       class="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                       placeholder="e.g., Hide toppings for small size" required>
            </div>

            <!-- Priority -->
            <div class="col-span-2">
                <label class="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <input type="number" name="modifier_rules[${modifierRuleCounter}][priority]"
                       value="0" min="0" max="100"
                       class="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
            </div>

            <!-- Applies To -->
            <div class="col-span-4">
                <label class="block text-xs font-medium text-gray-700 mb-1">Applies To</label>
                <select name="modifier_rules[${modifierRuleCounter}][applies_to]"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1.5">
                    <option value="all">All Items</option>
                    <option value="categories">Specific Categories</option>
                    <option value="products">Specific Products</option>
                    <option value="modifier_groups">Specific Modifier Groups</option>
                </select>
            </div>
        </div>

        <!-- Description -->
        <div class="mb-3">
            <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea name="modifier_rules[${modifierRuleCounter}][rule_description]"
                      rows="2" class="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                      placeholder="Describe what this rule does..."></textarea>
        </div>

        <!-- Trigger Conditions -->
        <div class="mb-3">
            <label class="block text-xs font-medium text-gray-700 mb-1">Trigger Conditions (JSON)</label>
            <textarea name="modifier_rules[${modifierRuleCounter}][trigger_conditions]"
                      rows="3" class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 font-mono"
                      placeholder='[{"type": "modifier_selected", "field": "modifier_id", "operator": "equals", "value": "1"}]'></textarea>
        </div>

        <!-- Actions -->
        <div class="mb-3">
            <label class="block text-xs font-medium text-gray-700 mb-1">Actions (JSON)</label>
            <textarea name="modifier_rules[${modifierRuleCounter}][actions]"
                      rows="3" class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 font-mono"
                      placeholder='[{"type": "show_hide", "parameters": {"action": "hide", "modifiers": [2, 3]}}]'></textarea>
        </div>

        <!-- Target IDs -->
        <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Target IDs (JSON Array)</label>
            <input type="text" name="modifier_rules[${modifierRuleCounter}][target_ids]"
                   class="w-full text-sm border border-gray-300 rounded px-2 py-1.5 font-mono"
                   placeholder='[1, 2, 3]'>
        </div>
    `;

    rulesContainer.appendChild(ruleDiv);
    modifierRules[modifierRuleCounter] = {
        rule_name: '',
        rule_description: '',
        trigger_conditions: [],
        actions: [],
        applies_to: 'all',
        target_ids: [],
        priority: 0
    };
}

function removeModifierRule(ruleId) {
    const ruleDiv = document.getElementById(`modifier-rule-${ruleId}`);
    if (ruleDiv) {
        ruleDiv.remove();
        delete modifierRules[ruleId];
    }
}

// =======================
// TOGGLE SWITCH FUNCTIONALITY
// =======================

/**
 * Initialize toggle switches
 */
function initializeToggleSwitches() {
    document.addEventListener('change', function(e) {
        if (e.target.classList.contains('toggle-input')) {
            applyToggleState(e.target);
        }
    });

    document.querySelectorAll('.toggle-input').forEach(toggle => applyToggleState(toggle));
}

// =======================
// PROPER FIELD DUPLICATION
// =======================

/**
 * Properly duplicate a field group with all its options and settings
 */
function duplicateFieldGroup(fieldGroupId) {
    const originalGroup = document.getElementById(`field-group-${fieldGroupId}`);
    if (!originalGroup) return;

    // Create new field group
    fieldGroupCounter++;
    optionCounters[fieldGroupCounter] = 0;

    const fieldsContainer = document.getElementById('fields-container');
    if (!fieldsContainer) return;

    // Generate new unique ID for the duplicated field
    const newFieldGroupId = Helpers.uniqueId();

    // Clone the original field group structure
    const newGroup = originalGroup.cloneNode(true);
    newGroup.id = `field-group-${fieldGroupCounter}`;
    newGroup.setAttribute('data-field-id', newFieldGroupId);
    newGroup.dataset.fieldKey = newFieldGroupId;

    // Update all form field names and IDs
    updateClonedFieldNames(newGroup, fieldGroupCounter);

    const hiddenFieldIdInput = newGroup.querySelector(`input[name="field_groups[${fieldGroupCounter}][field_id]"]`);
    if (hiddenFieldIdInput) {
        hiddenFieldIdInput.value = newFieldGroupId;
    }

    // Update header actions
    const duplicateBtn = newGroup.querySelector('[onclick*="duplicateFieldGroup"]');
    const deleteBtn = newGroup.querySelector('[onclick*="removeFieldGroup"]');
    if (duplicateBtn) duplicateBtn.setAttribute('onclick', `event.stopPropagation(); duplicateFieldGroup(${fieldGroupCounter})`);
    if (deleteBtn) deleteBtn.setAttribute('onclick', `event.stopPropagation(); removeFieldGroup(${fieldGroupCounter})`);

    // Update field group name
    const nameElement = newGroup.querySelector('.field-group-name');
    if (nameElement) {
        const originalName = originalGroup.querySelector('.field-group-name')?.textContent || 'Field';
        nameElement.textContent = originalName + ' (Copy)';
    }

    // Update field group description with new ID
    const descElement = newGroup.querySelector('.field-group-description');
    if (descElement) {
        const displayTypeSelect = newGroup.querySelector('select[name*="display_type"]');
        const displayType = displayTypeSelect ? displayTypeSelect.value : 'radio';
        descElement.textContent = `${displayType} • Field ${fieldGroupCounter} • ID: ${newFieldGroupId}`;
    }

    // Update options container and add option button
    const optionsContainer = newGroup.querySelector('[id*="options-container-"]');
    if (optionsContainer) {
        optionsContainer.id = `options-container-${fieldGroupCounter}`;

        // Update option counters based on existing options
        const optionRows = optionsContainer.querySelectorAll('.option-row');
        optionCounters[fieldGroupCounter] = optionRows.length;

        // Update each option row
        optionRows.forEach((optionRow, index) => {
            const optionId = `${fieldGroupCounter}_${index + 1}`;
            optionRow.id = `option-${optionId}`;
            optionRow.dataset.optionId = optionId;

            // Update remove button
            const removeBtn = optionRow.querySelector('[onclick*="removeOption"]');
            if (removeBtn) {
                removeBtn.setAttribute('onclick', `removeOption('${optionId}', ${fieldGroupCounter})`);
            }

            const nameInput = optionRow.querySelector(`input[name="field_groups[${fieldGroupCounter}][modifiers][${index + 1}][name]"]`);
            if (nameInput) {
                nameInput.addEventListener('input', () => refreshFieldConditionValueSources(fieldGroupCounter));
            }
        });
    }

    const addOptionBtn = newGroup.querySelector('[onclick*="addNewOption"]');
    if (addOptionBtn) {
        addOptionBtn.setAttribute('onclick', `addNewOption(${fieldGroupCounter})`);
    }

    const fieldConditionContainer = newGroup.querySelector(`#field-conditions-container-${fieldGroupCounter}`);
    if (fieldConditionContainer) {
        fieldConditionContainer.innerHTML = '';
    }
    const fieldConditionHelper = newGroup.querySelector(`#field-condition-helper-${fieldGroupCounter}`);
    if (fieldConditionHelper) {
        const defaultMessage = fieldConditionHelper.dataset.defaultMessage || fieldConditionHelper.textContent;
        fieldConditionHelper.textContent = defaultMessage;
        fieldConditionHelper.style.display = '';
    }
    const addFieldConditionButton = newGroup.querySelector(`#add-condition-btn-${fieldGroupCounter}`);
    if (addFieldConditionButton) {
        addFieldConditionButton.disabled = true;
        addFieldConditionButton.setAttribute('onclick', `addFieldCondition(${fieldGroupCounter})`);
    }
    fieldConditions[String(fieldGroupCounter)] = [];

    // Insert after the original
    originalGroup.insertAdjacentElement('afterend', newGroup);

    // Handle field type change to show/hide appropriate sections for the duplicated group
    handleFieldTypeChange(fieldGroupCounter);

    // Update overall group type
    refreshFieldGroupOrderUI();
    updateOverallGroupType();
    updateFieldConditionAvailability();
}

function updateClonedFieldNames(element, newFieldGroupId) {
    // Update input names
    element.querySelectorAll('input, select, textarea').forEach(field => {
        if (field.name && field.name.includes('field_groups[')) {
            field.name = field.name.replace(/field_groups\[\d+\]/, `field_groups[${newFieldGroupId}]`);
        }
    });

    // Update onchange handlers
    element.querySelectorAll('[onchange*="updateFieldGroup"]').forEach(field => {
        const onchange = field.getAttribute('onchange');
        if (onchange) {
            field.setAttribute('onchange', onchange.replace(/\d+/, newFieldGroupId));
        }
    });

    // Update handleFieldTypeChange handlers for display_type selects
    element.querySelectorAll('[onchange*="handleFieldTypeChange"]').forEach(field => {
        const onchange = field.getAttribute('onchange');
        if (onchange) {
            field.setAttribute('onchange', onchange.replace(/\d+/, newFieldGroupId));
        }
    });

    // Update text-settings div ID
    const textSettings = element.querySelector('[id*="text-settings-"]');
    if (textSettings) {
        textSettings.id = `text-settings-${newFieldGroupId}`;
    }

    // Update text-pricing-fields div ID
    const textPricingFields = element.querySelector('[id*="text-pricing-fields-"]');
    if (textPricingFields) {
        textPricingFields.id = `text-pricing-fields-${newFieldGroupId}`;
    }

    // Update text-price-symbol span ID
    const textPriceSymbol = element.querySelector('[id*="text-price-symbol-"]');
    if (textPriceSymbol) {
        textPriceSymbol.id = `text-price-symbol-${newFieldGroupId}`;
    }

    // Update text pricing onchange handlers
    element.querySelectorAll('[onchange*="toggleTextPricingFields"], [onchange*="updateTextPricingSymbol"]').forEach(field => {
        const onchange = field.getAttribute('onchange');
        if (onchange) {
            field.setAttribute('onchange', onchange.replace(/\d+/, newFieldGroupId));
        }
    });

    const conditionHelper = element.querySelector('[id^="field-condition-helper-"]');
    if (conditionHelper) {
        conditionHelper.id = `field-condition-helper-${newFieldGroupId}`;
    }

    const addConditionButton = element.querySelector('[id^="add-condition-btn-"]');
    if (addConditionButton) {
        addConditionButton.id = `add-condition-btn-${newFieldGroupId}`;
        const onclick = addConditionButton.getAttribute('onclick');
        if (onclick) {
            addConditionButton.setAttribute('onclick', onclick.replace(/\d+/, newFieldGroupId));
        }
    }

    const conditionContainer = element.querySelector('[id^="field-conditions-container-"]');
    if (conditionContainer) {
        conditionContainer.id = `field-conditions-container-${newFieldGroupId}`;
    }
}

// =======================
// FORM DATA SERIALIZATION
// =======================

/**
 * Serialize form data for backend submission
 */
function serializeModifierFormData() {
    const formData = {
        group_name: document.querySelector('input[name="group_name"]')?.value || '',
        group_description: document.querySelector('textarea[name="group_description"]')?.value || '',
        selection_type: document.querySelector('input[name="selection_type"]:checked')?.value || 'multiple',
        field_groups: {},
        modifier_conditions: {},
        modifier_rules: {}
    };

    // Serialize field groups
    document.querySelectorAll('[id^="field-group-"]').forEach(fieldGroup => {
        const groupId = fieldGroup.id.replace('field-group-', '');
        const fieldIdInput = fieldGroup.querySelector(`input[name="field_groups[${groupId}][field_id]"]`);
        const rawLabel = fieldGroup.querySelector(`input[name="field_groups[${groupId}][label]"]`)?.value || '';
        const fallbackLabel = fieldGroup.querySelector('.field-group-name')?.textContent?.trim() || '';
        const label = rawLabel.trim() || fallbackLabel;
        const groupData = {
            field_id: fieldIdInput?.value || fieldGroup.dataset.fieldKey || '',
            label,
            display_type: fieldGroup.querySelector(`select[name="field_groups[${groupId}][display_type]"]`)?.value || 'radio',
            instructions: fieldGroup.querySelector(`textarea[name="field_groups[${groupId}][instructions]"]`)?.value || '',
            is_required: fieldGroup.querySelector(`input[name="field_groups[${groupId}][is_required]"]`)?.checked || false,
            min_selections: fieldGroup.querySelector(`input[name="field_groups[${groupId}][min_selections]"]`)?.value || 0,
            max_selections: fieldGroup.querySelector(`input[name="field_groups[${groupId}][max_selections]"]`)?.value || 1,
            quantity_based: fieldGroup.querySelector(`input[name="field_groups[${groupId}][quantity_based]"]`)?.checked || false,
            hide_on_cart: fieldGroup.querySelector(`input[name="field_groups[${groupId}][hide_on_cart]"]`)?.checked || false,
            hide_on_checkout: fieldGroup.querySelector(`input[name="field_groups[${groupId}][hide_on_checkout]"]`)?.checked || false,
            hide_on_order: fieldGroup.querySelector(`input[name="field_groups[${groupId}][hide_on_order]"]`)?.checked || false,
            wrapper_width: fieldGroup.querySelector(`input[name="field_groups[${groupId}][wrapper_width]"]`)?.value || '',
            wrapper_class: fieldGroup.querySelector(`input[name="field_groups[${groupId}][wrapper_class]"]`)?.value || '',

            // Text field specific settings
            placeholder: fieldGroup.querySelector(`input[name="field_groups[${groupId}][placeholder]"]`)?.value || '',
            input_type: fieldGroup.querySelector(`select[name="field_groups[${groupId}][input_type]"]`)?.value || 'text',
            min_length: fieldGroup.querySelector(`input[name="field_groups[${groupId}][min_length]"]`)?.value || 0,
            max_length: fieldGroup.querySelector(`input[name="field_groups[${groupId}][max_length]"]`)?.value || null,
            regex_pattern: fieldGroup.querySelector(`input[name="field_groups[${groupId}][regex_pattern]"]`)?.value || '',
            adjust_pricing: fieldGroup.querySelector(`input[name="field_groups[${groupId}][adjust_pricing]"]`)?.checked || false,
            price_type: fieldGroup.querySelector(`select[name="field_groups[${groupId}][price_type]"]`)?.value || 'none',
            price: fieldGroup.querySelector(`input[name="field_groups[${groupId}][price]"]`)?.value || 0,

            modifiers: {}
        };

        // Serialize modifiers for this group (skip for text fields)
        if (groupData.display_type !== 'text') {
            fieldGroup.querySelectorAll('.option-row').forEach((optionRow, index) => {
                const optionName = optionRow.querySelector(`input[name*="[name]"]`)?.value?.trim() || '';
                if (!optionName) {
                    return;
                }

                const modifierIndex = index + 1;
                groupData.modifiers[modifierIndex] = {
                    id: optionRow.querySelector(`input[name*="[id]"]`)?.value || '',
                    name: optionName,
                    price_type: optionRow.querySelector(`select[name*="[price_type]"]`)?.value || 'fixed',
                    price: optionRow.querySelector(`input[name*="[price]"]`)?.value || 0,
                    included: optionRow.querySelector(`input[name*="[included]"]`)?.checked || false,
                    selected: optionRow.querySelector(`input[name*="[selected]"]`)?.checked || false
                };
            });
        }

        groupData.conditions = (fieldConditions[groupId] || []).map(condition => ({
            target_field_id: condition.target_field_id,
            operator: condition.operator,
            group_id: condition.group_id,
            value: condition.value,
            value_label: condition.value_label,
            values: condition.value
                ? [{ option_id: condition.value, option_label: condition.value_label || condition.value }]
                : []
        }));

        formData.field_groups[groupId] = groupData;
    });

    // Serialize modifier conditions
    document.querySelectorAll('[id^="modifier-condition-"]').forEach(condition => {
        const conditionId = condition.id.replace('modifier-condition-', '');
        formData.modifier_conditions[conditionId] = {
            condition_type: condition.querySelector(`select[name*="[condition_type]"]`)?.value || '',
            trigger_modifier_id: condition.querySelector(`select[name*="[trigger_modifier_id]"]`)?.value || '',
            trigger_value: condition.querySelector(`input[name*="[trigger_value]"]`)?.value || '',
            action_modifier_ids: Array.from(condition.querySelectorAll(`select[name*="[action_modifier_ids]"] option:checked`))
                .map(option => option.value),
            action_parameters: condition.querySelector(`textarea[name*="[action_parameters]"]`)?.value || '{}'
        };
    });

    // Serialize modifier rules
    document.querySelectorAll('[id^="modifier-rule-"]').forEach(rule => {
        const ruleId = rule.id.replace('modifier-rule-', '');
        formData.modifier_rules[ruleId] = {
            rule_name: rule.querySelector(`input[name*="[rule_name]"]`)?.value || '',
            rule_description: rule.querySelector(`textarea[name*="[rule_description]"]`)?.value || '',
            priority: rule.querySelector(`input[name*="[priority]"]`)?.value || 0,
            applies_to: rule.querySelector(`select[name*="[applies_to]"]`)?.value || 'all',
            trigger_conditions: rule.querySelector(`textarea[name*="[trigger_conditions]"]`)?.value || '[]',
            actions: rule.querySelector(`textarea[name*="[actions]"]`)?.value || '[]',
            target_ids: rule.querySelector(`input[name*="[target_ids]"]`)?.value || '[]'
        };
    });

    return formData;
}

/**
 * Submit modifier form data
 */
function submitModifierForm() {
    const formData = serializeModifierFormData();
    console.log('Serialized form data:', formData);

    // Here you would typically send the data to your backend
    // For now, we'll just log it and submit the actual form
    const form = document.querySelector('form');
    if (form) {
        form.submit();
    }
}

// Initialize toggle switches when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    initializeToggleSwitches();
    setupLayoutAndConditionalsCapture();
});

/**
 * Populate layout fields when editing
 */
window.populateLayoutFields = function(layoutData) {
    if (!layoutData || typeof layoutData !== 'object') return;

    // Populate label position
    const labelPositionSelect = document.querySelector('select[name="label_position"]');
    const labelPosition = layoutData.labels_position || layoutData.label_position;
    if (labelPositionSelect && labelPosition) {
        labelPositionSelect.value = labelPosition;
    }

    // Populate instruction position
    const instructionPositionSelect = document.querySelector('select[name="instruction_position"]');
    const instructionPosition = layoutData.instructions_position || layoutData.instruction_position;
    if (instructionPositionSelect && instructionPosition) {
        instructionPositionSelect.value = instructionPosition;
    }

    // Populate mark_required_fields checkbox
    const markRequiredCheckbox = document.querySelector('input[name="mark_required_fields"]');
    if (markRequiredCheckbox && layoutData.mark_required_fields) {
        markRequiredCheckbox.checked = true;
        applyToggleState(markRequiredCheckbox);
    }

    // Populate change_product_image checkbox
    const changeImageCheckbox = document.querySelector('input[name="change_product_image"]');
    if (changeImageCheckbox && layoutData.change_product_image) {
        changeImageCheckbox.checked = true;
        applyToggleState(changeImageCheckbox);
    }

    console.log('Layout fields populated');
};

/**
 * Populate conditional rules when editing
 */
window.populateConditionalRules = function(conditionalsData) {
    if (!conditionalsData || !conditionalsData.rules || conditionalsData.rules.length === 0) {
        return;
    }

    // Expand the conditions section since we have rules
    const conditionsContent = document.getElementById('conditions-content');
    const conditionsChevron = document.getElementById('conditions-chevron');
    if (conditionsContent) {
        conditionsContent.style.display = 'block';
    }
    if (conditionsChevron) {
        conditionsChevron.classList.add('rotate-180');
    }

    // Show the add state (hide start state)
    const startState = document.getElementById('conditions-start-state');
    const addState = document.getElementById('conditions-add-state');
    if (startState) startState.style.display = 'none';
    if (addState) addState.style.display = 'block';

    const container = document.getElementById('conditions-rules-container');
    if (!container) return;

    // Clear existing rules
    container.innerHTML = '';

    // Add each rule group
    conditionalsData.rules.forEach((ruleGroup, groupIndex) => {
        if (!ruleGroup.rules || ruleGroup.rules.length === 0) return;

        // Add OR separator if not first group
        if (groupIndex > 0) {
            const orSeparator = document.createElement('div');
            orSeparator.className = 'text-center py-2';
            orSeparator.innerHTML = '<span class="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">Or</span>';
            container.appendChild(orSeparator);
        }

        // Create condition group
        conditionGroupCounter++;
        const groupDiv = document.createElement('div');
        groupDiv.className = 'condition-group';
        groupDiv.dataset.groupId = conditionGroupCounter;
        groupDiv.innerHTML = '<div class="space-y-2 condition-rules-list"></div>';
        container.appendChild(groupDiv);

        const rulesList = groupDiv.querySelector('.condition-rules-list');

        // Add each rule in the group
        ruleGroup.rules.forEach((rule, ruleIndex) => {
            conditionRuleCounter++;
            const ruleDiv = document.createElement('div');
            ruleDiv.className = 'flex items-center space-x-3 condition-rule';
            ruleDiv.dataset.ruleId = conditionRuleCounter;

            // Map subjects back to field types
            const subjectToFieldType = {
                'product_cat': 'product_category',
                'product': 'product',
                'product_tag': 'product_tag',
                'product_type': 'product_type',
                'user_role': 'authentication'
            };
            const fieldType = subjectToFieldType[rule.subject] || 'product_category';

            ruleDiv.innerHTML = `
                <select class="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500" onchange="updateConditionField(${conditionRuleCounter}, this.value)">
                    <option value="product_category" ${fieldType === 'product_category' ? 'selected' : ''}>Product category</option>
                    <option value="product" ${fieldType === 'product' ? 'selected' : ''}>Product</option>
                    <option value="product_tag" ${fieldType === 'product_tag' ? 'selected' : ''}>Product tag</option>
                    <option value="product_type" ${fieldType === 'product_type' ? 'selected' : ''}>Product type</option>
                    <option value="authentication" ${fieldType === 'authentication' ? 'selected' : ''}>User Role</option>
                </select>
                <select class="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 condition-operator" onchange="updateConditionOperator(${conditionRuleCounter}, this.value)">
                    <option value="equals" ${(rule.operator === 'equals' || (!rule.operator && rule.condition)) ? 'selected' : ''}>Is equal to</option>
                    <option value="not_equals" ${rule.operator === 'not_equals' ? 'selected' : ''}>Is not equal to</option>
                    <option value="contains" ${rule.operator === 'contains' ? 'selected' : ''}>Contains</option>
                    <option value="not_contains" ${rule.operator === 'not_contains' ? 'selected' : ''}>Does not contain</option>
                </select>
                <div class="flex-1 relative condition-value-container"></div>
                <div class="flex items-center space-x-2">
                    <button type="button" class="and-button inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded hover:bg-blue-200" onclick="addConditionRule(${conditionGroupCounter})" style="display: ${ruleIndex === ruleGroup.rules.length - 1 ? 'inline-flex' : 'none'}">
                        And
                    </button>
                    <button type="button" class="text-gray-400 hover:text-red-600" onclick="removeConditionRule(this, ${conditionGroupCounter})">
                        <i class="uil uil-times text-lg"></i>
                    </button>
                </div>
            `;

            rulesList.appendChild(ruleDiv);

            // Render value control first
            const operator = ruleDiv.querySelector('.condition-operator').value;
            renderConditionValueControl(ruleDiv, operator, '');

            // Normalize value to [{id, text}] array for consistent handling
            const ruleValues = Array.isArray(rule.value)
                ? rule.value
                : (rule.value ? [{ id: String(rule.value), text: rule.value_label || String(rule.value) }] : []);

            // Populate the value(s) after a delay to ensure Select2 is initialized
            if (ruleValues.length > 0) {
                rule.value = ruleValues; // normalize for the block below
                setTimeout(() => {
                    const valueElement = ruleDiv.querySelector('[name="condition_value"]');

                    if (!valueElement) {
                        console.error('Value element not found for rule:', rule);
                        return;
                    }

                    console.log('Populating value element:', valueElement, 'with data:', rule.value);

                    if (window.jQuery && jQuery.fn && jQuery.fn.select2) {
                        // Prepare data for Select2
                        const select2Data = rule.value.map(v => ({
                            id: String(v.id || v.text),
                            text: String(v.text || v.id)
                        }));

                        console.log('Select2 data:', select2Data);

                        // Check if it's a select element (dropdown) or input (tags)
                        if (valueElement.tagName === 'SELECT') {
                            // For select dropdowns - add options and select them
                            select2Data.forEach(item => {
                                const option = new Option(item.text, item.id, true, true);
                                jQuery(valueElement).append(option);
                            });
                            jQuery(valueElement).trigger('change');
                        } else {
                            // For input fields with Select2 tags mode
                            // Destroy existing Select2 if any
                            if (jQuery(valueElement).data('select2')) {
                                jQuery(valueElement).select2('destroy');
                            }

                            // Initialize Select2 with the data
                            jQuery(valueElement).select2({
                                tags: true,
                                tokenSeparators: [','],
                                data: select2Data
                            });

                            // Set the selected values
                            jQuery(valueElement).val(select2Data.map(d => d.id)).trigger('change');
                        }

                        console.log('Value populated successfully');
                    } else {
                        // Fallback for non-Select2 elements
                        if (valueElement.tagName === 'SELECT') {
                            valueElement.value = rule.value[0].id || rule.value[0].text;
                        } else {
                            valueElement.value = rule.value.map(v => v.text || v.id).join(', ');
                        }
                    }
                }, 200);
            }
        });
    });

    console.log('Conditional rules populated');
};

/**
 * Capture layout and conditionals data before form submission
 */
function setupLayoutAndConditionalsCapture() {
    const form = document.getElementById('modifier-group-form');
    if (!form) return;

    form.addEventListener('submit', function(e) {
        e.preventDefault(); // Prevent default form submission

        console.log('[ModifierGroups] Submit intercepted, collecting data...');

        // Collect layout section data
        const layoutData = {
            labels_position: document.querySelector('select[name="label_position"]')?.value || 'above',
            instructions_position: document.querySelector('select[name="instruction_position"]')?.value || 'below',
            mark_required_fields: document.querySelector('input[name="mark_required_fields"]')?.checked || false,
            change_product_image: document.querySelector('input[name="change_product_image"]')?.checked || false,
            gallery_images: Array.isArray(window.groupLayoutData?.gallery_images) ? window.groupLayoutData.gallery_images : []
        };

        // Collect conditionals data from the rules section
        const conditionalsData = collectConditionalsData();

        // Collect field groups data (including field-level conditions)
        const fieldGroupsData = serializeModifierFormData();

        console.log('Layout data captured:', layoutData);
        console.log('Conditionals data captured:', conditionalsData);
        console.log('Field groups data captured:', fieldGroupsData.field_groups);

        // Get the group ID from form or URL (supports both numeric IDs and UUIDs)
        const formAction = form.action;
        // Match either numeric ID or UUID format
        const groupIdMatch = formAction.match(/\/modifiers\/([0-9a-f-]{36}|\d+)/i);
        const groupId = groupIdMatch?.[1];

        const setHiddenValue = (name, value) => {
            let input = form.querySelector(`input[name="${name}"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }
            input.value = value;
        };

        const fieldGroupsJson = JSON.stringify(fieldGroupsData.field_groups || {});
        console.log('[ModifierGroups] field_groups_json value:', fieldGroupsJson);
        console.log('[ModifierGroups] field_groups keys:', Object.keys(fieldGroupsData.field_groups || {}));

        setHiddenValue('field_groups_json', fieldGroupsJson);
        setHiddenValue('layout_sections', JSON.stringify(layoutData));
        setHiddenValue('conditionals', JSON.stringify(conditionalsData));

        // Verify hidden input was set
        const verifyInput = form.querySelector('input[name="field_groups_json"]');
        console.log('[ModifierGroups] Hidden input value length:', verifyInput?.value?.length);

        form.submit();
    });
}

/**
 * Collect conditionals/rules data from the form
 */
function collectConditionalsData() {
    const rules = [];
    const rulesContainer = document.getElementById('conditions-rules-container');

    if (!rulesContainer) {
        return { rules: [], match_type: 'all' };
    }

    // Collect all condition groups (OR groups)
    rulesContainer.querySelectorAll('.condition-group[data-group-id]').forEach((conditionGroup) => {
        const groupRules = [];

        // Collect all rules within this condition group (AND rules)
        conditionGroup.querySelectorAll('.condition-rule[data-rule-id]').forEach((ruleElement) => {
            const ruleId = ruleElement.dataset.ruleId;
            const fieldSelect = ruleElement.querySelector('select:first-child');
            const operatorSelect = ruleElement.querySelector('.condition-operator');
            const valueElement = ruleElement.querySelector('[name="condition_value"]');

            if (!fieldSelect || !valueElement) return;

            const fieldType = fieldSelect.value;
            const operator = operatorSelect ? operatorSelect.value : 'equals';

            // Map field types to subjects and conditions
            const fieldMapping = {
                'product_category': { subject: 'product_cat', condition: 'product_cats' },
                'product': { subject: 'product', condition: 'products' },
                'product_tag': { subject: 'product_tag', condition: 'p_tags' },
                'product_type': { subject: 'product_type', condition: 'product_types' },
                'authentication': { subject: 'user_role', condition: 'user_roles' }
            };

            const mapping = fieldMapping[fieldType] || { subject: fieldType, condition: fieldType };

            // Collect values - handle both Select2 and regular inputs
            let values = [];
            if (window.jQuery && jQuery.fn && jQuery.fn.select2 && jQuery(valueElement).data('select2')) {
                // Select2 is initialized - get selected data
                const selectedData = jQuery(valueElement).select2('data');
                values = selectedData.map(item => ({
                    id: item.id || item.text,
                    text: item.text || item.id
                }));
            } else {
                // Regular select or input
                if (valueElement.tagName === 'SELECT') {
                    const selected = Array.from(valueElement.selectedOptions);
                    values = selected.map(opt => ({
                        id: opt.value,
                        text: opt.textContent
                    }));
                } else if (valueElement.value) {
                    // Regular input - split by comma if multiple values
                    const vals = valueElement.value.split(',').map(v => v.trim()).filter(v => v);
                    values = vals.map(v => ({
                        id: v,
                        text: v
                    }));
                }
            }

            if (values.length > 0) {
                groupRules.push({
                    subject: mapping.subject,
                    condition: mapping.condition,
                    operator: operator,  // Include the operator (equals, not_equals, contains, not_contains)
                    value: values
                });
            }
        });

        if (groupRules.length > 0) {
            rules.push({
                rules: groupRules
            });
        }
    });

    return {
        rules: rules,
        match_type: 'all'
    };
}

// =======================
// ADVANCED CONDITIONAL ACTIONS
// =======================

/**
 * Add advanced conditional action builder
 */
function addAdvancedCondition() {
    advancedConditionCounter++;
    const conditionsContainer = document.getElementById('advanced-conditions-container');

    if (!conditionsContainer) return;

    const conditionDiv = document.createElement('div');
    conditionDiv.className = 'border border-blue-200 rounded-lg p-4 mb-4 bg-blue-50';
    conditionDiv.id = `advanced-condition-${advancedConditionCounter}`;

    conditionDiv.innerHTML = `
        <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-medium text-blue-800">Advanced Condition ${advancedConditionCounter}</h4>
            <button type="button" onclick="removeAdvancedCondition(${advancedConditionCounter})"
                    class="text-blue-400 hover:text-red-600 text-sm">
                Remove
            </button>
        </div>

        <!-- Condition Builder -->
        <div class="space-y-4">
            <!-- IF Section -->
            <div class="bg-white rounded border p-3">
                <div class="flex items-center gap-2 mb-2">
                    <span class="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded">IF</span>
                    <select class="text-xs border border-gray-300 rounded px-2 py-1"
                            onchange="updateAdvancedConditionTrigger(${advancedConditionCounter}, this.value)">
                        <option value="modifier_selected">Modifier is selected</option>
                        <option value="modifier_quantity">Modifier quantity</option>
                        <option value="total_price">Total price</option>
                        <option value="group_selection_count">Group selection count</option>
                    </select>
                </div>

                <div id="trigger-details-${advancedConditionCounter}" class="grid grid-cols-3 gap-2">
                    <select class="text-xs border border-gray-300 rounded px-2 py-1">
                        <option value="">Select modifier...</option>
                    </select>
                    <select class="text-xs border border-gray-300 rounded px-2 py-1">
                        <option value="equals">equals</option>
                        <option value="greater_than">greater than</option>
                        <option value="less_than">less than</option>
                        <option value="not_equals">not equals</option>
                    </select>
                    <input type="text" class="text-xs border border-gray-300 rounded px-2 py-1" placeholder="Value">
                </div>
            </div>

            <!-- THEN Section -->
            <div class="bg-white rounded border p-3">
                <div class="flex items-center gap-2 mb-2">
                    <span class="text-xs font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded">THEN</span>
                    <select class="text-xs border border-gray-300 rounded px-2 py-1"
                            onchange="updateAdvancedConditionAction(${advancedConditionCounter}, this.value)">
                        <option value="show_modifiers">Show modifiers</option>
                        <option value="hide_modifiers">Hide modifiers</option>
                        <option value="require_modifiers">Require modifiers</option>
                        <option value="disable_modifiers">Disable modifiers</option>
                        <option value="set_price">Set price</option>
                        <option value="add_price">Add to price</option>
                        <option value="multiply_price">Multiply price</option>
                        <option value="show_message">Show message</option>
                    </select>
                </div>

                <div id="action-details-${advancedConditionCounter}" class="space-y-2">
                    <select multiple class="w-full text-xs border border-gray-300 rounded px-2 py-1" size="3">
                        <option value="">Select modifiers to affect...</option>
                    </select>
                </div>
            </div>

            <!-- Advanced Options -->
            <div class="bg-gray-50 rounded border p-3">
                <details class="group">
                    <summary class="text-xs font-medium text-gray-700 cursor-pointer">Advanced Options</summary>
                    <div class="mt-2 space-y-2">
                        <label class="flex items-center gap-2">
                            <input type="checkbox" class="text-xs">
                            <span class="text-xs">Execute only once per session</span>
                        </label>
                        <label class="flex items-center gap-2">
                            <input type="checkbox" class="text-xs">
                            <span class="text-xs">Reverse condition when trigger is deselected</span>
                        </label>
                        <div>
                            <label class="block text-xs font-medium text-gray-700 mb-1">Delay (ms)</label>
                            <input type="number" value="0" min="0" max="5000"
                                   class="w-full text-xs border border-gray-300 rounded px-2 py-1">
                        </div>
                        <div>
                            <label class="block text-xs font-medium text-gray-700 mb-1">Animation</label>
                            <select class="w-full text-xs border border-gray-300 rounded px-2 py-1">
                                <option value="none">None</option>
                                <option value="fade">Fade In/Out</option>
                                <option value="slide">Slide In/Out</option>
                                <option value="bounce">Bounce</option>
                            </select>
                        </div>
                    </div>
                </details>
            </div>
        </div>

        <!-- JSON Preview -->
        <div class="mt-4">
            <details class="group">
                <summary class="text-xs font-medium text-gray-700 cursor-pointer">JSON Preview</summary>
                <textarea id="json-preview-${advancedConditionCounter}"
                          class="mt-2 w-full text-xs border border-gray-300 rounded px-2 py-1 font-mono bg-gray-100"
                          rows="4" readonly></textarea>
            </details>
        </div>
    `;

    conditionsContainer.appendChild(conditionDiv);
    updateAdvancedConditionPreview(advancedConditionCounter);
}

function removeAdvancedCondition(conditionId) {
    const conditionDiv = document.getElementById(`advanced-condition-${conditionId}`);
    if (conditionDiv) {
        conditionDiv.remove();
    }
}

function updateAdvancedConditionTrigger(conditionId, triggerType) {
    const detailsContainer = document.getElementById(`trigger-details-${conditionId}`);
    if (!detailsContainer) return;

    let html = '';

    switch (triggerType) {
        case 'modifier_selected':
            html = `
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="">Select modifier...</option>
                    ${getModifierOptionsForCondition()}
                </select>
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="is">is</option>
                    <option value="is_not">is not</option>
                </select>
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="selected">selected</option>
                    <option value="deselected">deselected</option>
                </select>
            `;
            break;

        case 'modifier_quantity':
            html = `
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="">Select modifier...</option>
                    ${getModifierOptionsForCondition()}
                </select>
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="equals">equals</option>
                    <option value="greater_than">greater than</option>
                    <option value="less_than">less than</option>
                </select>
                <input type="number" min="0" value="1" class="text-xs border border-gray-300 rounded px-2 py-1" placeholder="Quantity">
            `;
            break;

        case 'total_price':
            html = `
                <span class="text-xs text-gray-500 px-2 py-1">Total price</span>
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="equals">equals</option>
                    <option value="greater_than">greater than</option>
                    <option value="less_than">less than</option>
                </select>
                <input type="number" step="0.01" min="0" class="text-xs border border-gray-300 rounded px-2 py-1" placeholder="Amount">
            `;
            break;

        case 'group_selection_count':
            html = `
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="">Select group...</option>
                    ${getGroupOptionsForCondition()}
                </select>
                <select class="text-xs border border-gray-300 rounded px-2 py-1">
                    <option value="equals">has exactly</option>
                    <option value="greater_than">has more than</option>
                    <option value="less_than">has fewer than</option>
                </select>
                <input type="number" min="0" value="1" class="text-xs border border-gray-300 rounded px-2 py-1" placeholder="Count">
            `;
            break;
    }

    detailsContainer.innerHTML = html;
    updateAdvancedConditionPreview(conditionId);
}

function updateAdvancedConditionAction(conditionId, actionType) {
    const detailsContainer = document.getElementById(`action-details-${conditionId}`);
    if (!detailsContainer) return;

    let html = '';

    switch (actionType) {
        case 'show_modifiers':
        case 'hide_modifiers':
        case 'require_modifiers':
        case 'disable_modifiers':
            html = `
                <select multiple class="w-full text-xs border border-gray-300 rounded px-2 py-1" size="3">
                    ${getModifierOptionsForCondition()}
                </select>
                <p class="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
            `;
            break;

        case 'set_price':
        case 'add_price':
        case 'multiply_price':
            html = `
                <div class="grid grid-cols-2 gap-2">
                    <select class="text-xs border border-gray-300 rounded px-2 py-1">
                        ${getModifierOptionsForCondition()}
                    </select>
                    <input type="number" step="0.01" class="text-xs border border-gray-300 rounded px-2 py-1"
                           placeholder="${actionType === 'multiply_price' ? 'Multiplier' : 'Amount'}">
                </div>
            `;
            break;

        case 'show_message':
            html = `
                <textarea class="w-full text-xs border border-gray-300 rounded px-2 py-1"
                          rows="2" placeholder="Message to display to user"></textarea>
                <select class="w-full text-xs border border-gray-300 rounded px-2 py-1 mt-1">
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                    <option value="success">Success</option>
                </select>
            `;
            break;
    }

    detailsContainer.innerHTML = html;
    updateAdvancedConditionPreview(conditionId);
}

function getModifierOptionsForCondition() {
    let options = '<option value="">Select modifier...</option>';

    document.querySelectorAll('[id^="field-group-"]').forEach((fieldGroup, groupIndex) => {
        const groupName = fieldGroup.querySelector('.field-group-name')?.textContent || `Group ${groupIndex + 1}`;
        fieldGroup.querySelectorAll('.option-row').forEach((optionRow, optionIndex) => {
            const optionName = optionRow.querySelector('input[type="text"]')?.value || `Option ${optionIndex + 1}`;
            options += `<option value="${groupIndex}_${optionIndex}">${groupName}: ${optionName}</option>`;
        });
    });

    return options;
}

function getGroupOptionsForCondition() {
    let options = '<option value="">Select group...</option>';

    document.querySelectorAll('[id^="field-group-"]').forEach((fieldGroup, groupIndex) => {
        const groupName = fieldGroup.querySelector('.field-group-name')?.textContent || `Group ${groupIndex + 1}`;
        options += `<option value="${groupIndex}">${groupName}</option>`;
    });

    return options;
}

function updateAdvancedConditionPreview(conditionId) {
    const previewElement = document.getElementById(`json-preview-${conditionId}`);
    if (!previewElement) return;

    const sampleCondition = {
        id: conditionId,
        type: "advanced_condition",
        trigger: {
            type: "modifier_selected",
            modifier_id: "1_1",
            operator: "is",
            value: "selected"
        },
        action: {
            type: "show_modifiers",
            target_modifiers: ["1_2", "1_3"],
            parameters: {
                animation: "fade",
                delay: 0,
                reverse_on_deselect: true
            }
        },
        options: {
            execute_once: false,
            reverse_condition: true,
            delay: 0,
            animation: "none"
        }
    };

    previewElement.value = JSON.stringify(sampleCondition, null, 2);
}

// =======================
// CONDITION EVALUATION ENGINE
// =======================

/**
 * Simple condition evaluation engine for frontend preview
 */
class ConditionEvaluator {
    constructor() {
        this.selectedModifiers = new Set();
        this.modifierQuantities = new Map();
        this.conditions = new Map();
    }

    addCondition(condition) {
        this.conditions.set(condition.id, condition);
    }

    removeCondition(conditionId) {
        this.conditions.delete(conditionId);
    }

    updateModifierSelection(modifierId, selected, quantity = 1) {
        if (selected) {
            this.selectedModifiers.add(modifierId);
            this.modifierQuantities.set(modifierId, quantity);
        } else {
            this.selectedModifiers.delete(modifierId);
            this.modifierQuantities.delete(modifierId);
        }

        this.evaluateAllConditions();
    }

    evaluateAllConditions() {
        this.conditions.forEach((condition, conditionId) => {
            const result = this.evaluateCondition(condition);
            if (result) {
                this.executeAction(condition.action);
            }
        });
    }

    evaluateCondition(condition) {
        const trigger = condition.trigger;

        switch (trigger.type) {
            case 'modifier_selected':
                return this.selectedModifiers.has(trigger.modifier_id) === (trigger.value === 'selected');

            case 'modifier_quantity':
                const quantity = this.modifierQuantities.get(trigger.modifier_id) || 0;
                return this.compareValues(quantity, trigger.value, trigger.operator);

            case 'total_price':
                const totalPrice = this.calculateTotalPrice();
                return this.compareValues(totalPrice, trigger.value, trigger.operator);

            case 'group_selection_count':
                const groupCount = this.getGroupSelectionCount(trigger.group_id);
                return this.compareValues(groupCount, trigger.value, trigger.operator);

            default:
                return false;
        }
    }

    compareValues(actual, expected, operator) {
        switch (operator) {
            case 'equals': return actual == expected;
            case 'greater_than': return actual > expected;
            case 'less_than': return actual < expected;
            case 'not_equals': return actual != expected;
            default: return false;
        }
    }

    executeAction(action) {
        switch (action.type) {
            case 'show_modifiers':
                this.showModifiers(action.target_modifiers);
                break;
            case 'hide_modifiers':
                this.hideModifiers(action.target_modifiers);
                break;
            case 'require_modifiers':
                this.requireModifiers(action.target_modifiers);
                break;
            case 'disable_modifiers':
                this.disableModifiers(action.target_modifiers);
                break;
            // Add more action implementations as needed
        }
    }

    showModifiers(modifierIds) {
        modifierIds.forEach(id => {
            const element = document.querySelector(`[data-modifier-id="${id}"]`);
            if (element) {
                element.style.display = 'block';
                element.classList.remove('hidden');
            }
        });
    }

    hideModifiers(modifierIds) {
        modifierIds.forEach(id => {
            const element = document.querySelector(`[data-modifier-id="${id}"]`);
            if (element) {
                element.style.display = 'none';
                element.classList.add('hidden');
            }
        });
    }

    calculateTotalPrice() {
        // Implementation would calculate total price based on selected modifiers
        return 0;
    }

    getGroupSelectionCount(groupId) {
        // Implementation would count selections in a specific group
        return 0;
    }
}

// Global condition evaluator instance
const conditionEvaluator = new ConditionEvaluator();

// Add to global exports
if (typeof window !== 'undefined') {
    window.addModifierCondition = addModifierCondition;
    window.removeModifierCondition = removeModifierCondition;
    window.addModifierRule = addModifierRule;
    window.removeModifierRule = removeModifierRule;
    window.serializeModifierFormData = serializeModifierFormData;
    window.submitModifierForm = submitModifierForm;
    window.addAdvancedCondition = addAdvancedCondition;
    window.removeAdvancedCondition = removeAdvancedCondition;
    window.updateAdvancedConditionTrigger = updateAdvancedConditionTrigger;
    window.updateAdvancedConditionAction = updateAdvancedConditionAction;
    window.ConditionEvaluator = ConditionEvaluator;
    window.conditionEvaluator = conditionEvaluator;
}
